<template>
  <div class="text-center mr-2">
    <!-- The panel stays open while a formats side menu is up (close-on-content-click off), so the
         rows close it themselves once an action ran -->
    <v-menu v-model="menuOpen" offset-y left :close-on-content-click="false" content-class="silo-action-menu">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          text
          small
          color="profileAnchorColor"
          v-bind="attrs" v-on="on"
          @click="$emit('refreshData')"
        >
          <v-icon v-if="isMissionControlSession">mdi-dots-horizontal</v-icon>
          <i v-else class="material-icons-outlined">file_download</i>
        </v-btn>
      </template>
      <!-- design "Download content" (POP Authority Deliverables, shared with White Glove, 2026-10-21):
           one heading per page type with its reports indented under it, a download icon at the right of
           every downloadable row; the file formats open in a side menu -->
      <v-list class="silo-action-menu__list">
        <template v-for="(item, index) in visibleMenuItems">
          <v-list-item
            v-if="item.heading"
            :key="index"
            class="silo-action-menu__heading"
            :class="{ 'basic-divider': item.divider }"
          >
            <v-list-item-title class="silo-action-menu__title">{{ item.title }}</v-list-item-title>
          </v-list-item>

          <!-- the whole row opens the formats menu, which then sits flush against the panel's edge -->
          <v-menu
            v-else-if="item.children"
            :key="index"
            offset-x
            left
            content-class="silo-action-menu silo-action-menu--formats"
          >
            <template v-slot:activator="{ on: x, attrs: y }">
              <v-list-item
                class="silo-action-menu__item"
                :class="{ 'basic-divider': item.divider, 'silo-action-menu__item--child': item.child }"
                :disabled="item.isDisabled"
                v-bind="y"
                v-on="x"
              >
                <span class="silo-action-menu__row">
                  <i v-if="item.icon" class="material-icons-outlined silo-action-menu__lead">{{ item.icon }}</i>
                  <v-icon v-else-if="item.vIcon" class="silo-action-menu__lead">{{ item.vIcon }}</v-icon>
                  <v-list-item-title class="silo-action-menu__title" :class="item.class || ''">
                    {{ item.title }}
                  </v-list-item-title>
                  <i class="material-icons silo-action-menu__download" aria-hidden="true">file_download</i>
                </span>
              </v-list-item>
            </template>
            <v-list class="silo-action-menu__list silo-action-menu__list--formats">
              <v-list-item v-for="(subItem, subIndex) in item.children" :key="subIndex"
                class="silo-action-menu__item silo-action-menu__item--format"
                @click="runAction(subItem.onClick, subItem.params)">
                <v-list-item-title class="silo-action-menu__title">
                  {{ subItem.title }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-list-item
            v-else
            :key="index"
            class="silo-action-menu__item"
            :class="{ 'basic-divider': item.divider }"
            :disabled="item.isDisabled"
            @click="runAction(item.onClick, item.params)"
          >
            <span class="silo-action-menu__row">
              <i v-if="item.icon" class="material-icons-outlined silo-action-menu__lead">{{ item.icon }}</i>
              <v-icon v-else-if="item.vIcon" color="#000000" class="silo-action-menu__lead">{{ item.vIcon }}</v-icon>
              <v-list-item-title class="silo-action-menu__title" :class="item.class || ''">
                {{ item.title }}
              </v-list-item-title>
            </span>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import moment from "moment";
import { isEmpty } from "lodash";
import {
  downloadFile,
  downloadWordFile,
  downloadEditorReport,
  DOCX_LABEL_VALUE_COLUMN_WIDTHS,
  DOCX_SINGLE_COLUMN_WIDTHS,
  generateSchemasJSON,
  generateSchemasWord,
  getDocxColumnWidths,
  getDocxFullWidthImageTransformation,
  getDocxWeightedColumnWidths,
  getReportHeaderImage,
} from "@/utils/download-reports.js"
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  ShadingType,
  AlignmentType,
  ImageRun,
  ExternalHyperlink,
  WidthType,
  BorderStyle,
  HeadingLevel,
  convertInchesToTwip,
  PageBreak,
  TableLayoutType,
  HorizontalPositionRelativeFrom,
  VerticalPositionRelativeFrom,
  TextWrappingType,
  TextWrappingSide
} from "docx";
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import eeatPdfHeader from '../../../../public/static/eeat-pdf-header.png';
import bulbIcon from '../../../../public/static/bulb-icon.png';
import eeatWorldIcon from '../../../../public/static/eeat-world-icon.png';
import wordCountIcon from '../../../../public/static/word-count-icon.png';
import orangeKeyIcon from '../../../../public/static/orange-key-icon.png';
import pageIcon from '../../../../public/static/page-icon.png';
import checkMarkIcon from '../../../../public/static/check_mark_icon.png';
import greenInfoIcon from '../../../../public/static/green_info_icon.png';
import eeatKeywordWarningIcon from '../../../../public/static/eeat-keyword-warning-icon.png';
import eeatKeywordPresentIcon from '../../../../public/static/eeat-keyword-present-icon.png';
import wordDocHeadImg from '../../../../public/static/word-doc-head-image.png';
import explainData from '@/components/PageRun/eat-data/EatSchemaExplanation';
import pdfHeaderLogo from '../../../../public/static/schema-page-run-pdf-logo.png';
import { ReportTypes } from "../../PageRun/constants";
import { pageScoreTintColor } from '@/utils';

/** Server sends hasGuardDiffData; fall back to inspecting the (possibly slimmed) blob. */
function keywordHasGuardDiffData (siloKeyword) {
  return siloKeyword.hasGuardDiffData ?? !isEmpty(siloKeyword.guardDiffData)
}

const DOCX_SECTION_HEADER_COLUMN_WIDTHS = getDocxWeightedColumnWidths([10000, 3300]);
const DOCX_SCHEMA_INFO_COLUMN_WIDTHS = getDocxColumnWidths(2);
const DOCX_SCHEMA_TYPE_COLUMN_WIDTHS = getDocxWeightedColumnWidths([2500, 6500]);
const DOCX_EEAT_SIGNAL_COLUMN_WIDTHS = getDocxWeightedColumnWidths([1, 3, 1]);


function parseHtmlData(input) {
  if (Array.isArray(input) && input.every(r => Array.isArray(r))) {
    return [buildDocxTableFrom2D(input)];
  }

  if (typeof input === "string" && /^\s*\[/.test(input.trim())) {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed) && parsed.every(r => Array.isArray(r))) {
        return [buildDocxTableFrom2D(parsed)];
      }
    } catch { ' ' }
  }

  const html = typeof input === "string" ? input : String(input ?? "");
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const htmlTable = doc.querySelector("table");
  if (htmlTable) {
    return [buildDocxTableFromHtmlTable(htmlTable)];
  }

  const out = [];
  const faqItems = doc.querySelectorAll(".faq-item");
  if (faqItems.length > 0) {
    faqItems.forEach(item => {
      let q = "";
      let aNode = null;

      const questionHeader = item.querySelector(".question h4");
      const answerContainer = item.querySelector(".answer");

      if (questionHeader && answerContainer) {
        q = questionHeader.textContent.trim();
        aNode = answerContainer;
      }
      else {
        const strong = item.querySelector("strong");
        if (strong) {
          q = strong.textContent.trim();
        }
        const firstParagraph = item.querySelector("p");
        if (firstParagraph) {
          aNode = { childNodes: [firstParagraph] }; 
        }
      }

      if (q) {
        out.push(
          new Paragraph({
            children: [new TextRun({ text: q, bold: true, size: 24 })],
            spacing: { before: 200, after: 100 },
          })
        );
      }

  if (aNode && aNode.childNodes) {
    aNode.childNodes.forEach(node => {
      if (node.nodeName === "UL" || node.nodeName === "OL") {
        node.querySelectorAll("li").forEach(li => {
          out.push(
            new Paragraph({
              text: li.textContent.trim(),
              bullet: { level: 0 },
              spacing: { after: 60 },
            })
          );
        });
      } else if (node.nodeName === "PRE") {
        const lines = node.textContent.trim().split("\n");
        lines.forEach(line => {
          if (line.startsWith("-")) {
            out.push(
              new Paragraph({
                text: line.replace(/^-+\s*/, "").trim(),
                bullet: { level: 0 },
                spacing: { after: 60 },
              })
            );
          } else if (line.trim()) {
            out.push(
              new Paragraph({
                children: [new TextRun({ text: line.trim(), size: 20 })],
                spacing: { after: 60 },
              })
            );
          }
        });
      } else {
        const text = node.textContent?.trim();
        if (text) {
          out.push(
            new Paragraph({
              children: [new TextRun({ text, size: 20 })],
              spacing: { after: 60 },
            })
          );
        }
      }
    });
  }

      out.push(new Paragraph({ spacing: { after: 120 } }));
    });
    return out;
  }
   doc.body.childNodes.forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE && node.nodeName.toLowerCase() === "p") {
      const paragraphContent = processParagraphWithBreaks(node);
      out.push(new Paragraph({
        children: paragraphContent
      }));
    } else {
      out.push(new Paragraph({ children: parseNodeToTextRuns(node) }));
    }
  });
  return out;
}
  function processParagraphWithBreaks(paragraphNode) {
    const runs = [];
    let currentText = '';
    
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        currentText += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.nodeName.toLowerCase();
        
        switch (tag) {
          case "br":
            if (currentText.trim()) {
              runs.push(new TextRun({ text: currentText }));
              currentText = '';
            }
            runs.push(new TextRun({ text: "", break: 1 })); // Add line break
            break;
            
          case "b":
          case "strong":
            if (currentText.trim()) {
              runs.push(new TextRun({ text: currentText }));
              currentText = '';
            }
            runs.push(new TextRun({ text: node.textContent, bold: true }));
            break;
            
          case "i":
          case "em":
            if (currentText.trim()) {
              runs.push(new TextRun({ text: currentText }));
              currentText = '';
            }
            runs.push(new TextRun({ text: node.textContent, italics: true }));
            break;
            
          case "u":
            if (currentText.trim()) {
              runs.push(new TextRun({ text: currentText }));
              currentText = '';
            }
            runs.push(new TextRun({ text: node.textContent, underline: {} }));
            break;
            
          default:
            node.childNodes.forEach(processNode);
        }
      }
    };
    
    paragraphNode.childNodes.forEach(processNode);
    
    if (currentText.trim()) {
      runs.push(new TextRun({ text: currentText }));
    }
    
    return runs;
  }



function buildDocxTableFrom2D(cellRows, isComplex = false) {

  if (!isComplex) {
    // Original behavior: cell data is plain strings
    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top:    { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        left:   { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        right:  { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        insideH:{ style: BorderStyle.SINGLE, size: 4, color: "DDDDDD" },
        insideV:{ style: BorderStyle.SINGLE, size: 4, color: "DDDDDD" },
      },
      rows: cellRows.map(row =>
        new TableRow({
          children: row.map(cellText => {
            const text = cellText == null ? "" : String(cellText);
            return new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text, bold: false })] })],
              margins: { top: 120, bottom: 120, left: 120, right: 120 },
            });
          }),
        })
      ),
    });
  } else {
    // New behavior: cellRows already contain TableCell objects
    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top:    { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        left:   { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        right:  { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" },
        insideH:{ style: BorderStyle.SINGLE, size: 4, color: "DDDDDD" },
        insideV:{ style: BorderStyle.SINGLE, size: 4, color: "DDDDDD" },
      },
      rows: cellRows.map(row => new TableRow({ children: row })),
    });
  }
}

function buildDocxTableFromHtmlTable(tbl) {

  const rows = Array.from(tbl.querySelectorAll("tr")).map(tr => {
    return Array.from(tr.querySelectorAll("th, td")).map(td => {
      const children = [];

      // Process all child nodes (text, <ul>, <ol>, <p>, etc.)
      function processNode(node) {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            children.push(new Paragraph({ children: [new TextRun(text)] }));
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.tagName === "UL" || node.tagName === "OL") {
            const isOrdered = node.tagName === "OL";
            node.querySelectorAll("li").forEach(li => {
              children.push(
                new Paragraph({
                  text: li.textContent.trim(),
                  bullet: !isOrdered ? { level: 0 } : undefined,
                  numbering: isOrdered ? { numId: 1, level: 0 } : undefined, // needs numbering def
                })
              );
            });
          } else {
            // For <p>, <b>, <i>, or plain div/span
            children.push(
              new Paragraph({
                children: [new TextRun(node.textContent.trim())],
              })
            );
          }
        }
      }

      td.childNodes.forEach(processNode);

      if (children.length === 0) {
        children.push(new Paragraph(""));
      }

      return new TableCell({
        children,
        margins: { top: 120, bottom: 120, left: 120, right: 120 },
      });
    });
  });

  return buildDocxTableFrom2D(rows, true); // Pass flag if needed, or modify buildDocxTableFrom2D
}

function parseNodeToTextRuns(node) {
  const runs = [];
  node.childNodes?.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      const t = (child.textContent ?? "").replace(/\s+/g, " ");
      if (t) runs.push(new TextRun({ text: t }));
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.nodeName.toLowerCase();
      switch (tag) {
        case "b":
        case "strong":
          runs.push(new TextRun({ text: child.textContent ?? "", bold: true }));
          break;
        case "i":
        case "em":
          runs.push(new TextRun({ text: child.textContent ?? "", italics: true }));
          break;
        case "u":
          runs.push(new TextRun({ text: child.textContent ?? "", underline: {} }));
          break;
         case "br":
          runs.push(new TextRun({ text: "", break: 1 })); // Line break
          break;
        case "img":
          runs.push(new TextRun({ text: "🖼️ <image: your image file link here>" }));
          break;
        case "form":
          runs.push(new TextRun({ text: "<form: your form here>" }));
          break;
        case "p":
          runs.push(new TextRun({ text: child.textContent ?? "" }));
          break;
        case "span":
        case "div":
          runs.push(...parseNodeToTextRuns(child));
          break;
        default:
          runs.push(new TextRun({ text: child.textContent ?? "" }));
      }
    }
  });
  return runs;
}

function htmlStringToTextRuns(htmlString) {
  if (typeof htmlString !== 'string') {
    return [];
  }
  
  htmlString = htmlString.trim();
  if (!htmlString) {
    return [];
  }
  
  if (!/<[^>]+>/.test(htmlString)) {
    return [{ text: htmlString, bold: false }];
  }
  
  let doc;
  try {
    const parser = new DOMParser();
    doc = parser.parseFromString(htmlString, 'text/html');
    
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      console.warn('HTML parsing error, falling back to plain text:', htmlString);
      const fallbackDoc = parser.parseFromString(htmlString, 'text/html');
      return [{ text: (fallbackDoc.body && fallbackDoc.body.textContent ? fallbackDoc.body.textContent : '').trim(), bold: false }];
    }
  } catch (error) {
    console.warn('Error parsing HTML, falling back to plain text:', error);
    const fallbackDoc = new DOMParser().parseFromString(htmlString, 'text/html');
    return [{ text: (fallbackDoc.body && fallbackDoc.body.textContent ? fallbackDoc.body.textContent : '').trim(), bold: false }];
  }
  const result = [];
  
  const processElement = (element) => {
    const textRuns = [];
    const listChildren = [];
    let hasList = false;
    
    const extractTextRuns = (node, isBold = false) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          textRuns.push({ text: text + ' ', bold: isBold });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.nodeName.toLowerCase();
        
        switch (tag) {
          case 'strong':
          case 'b':
            node.childNodes.forEach(child => extractTextRuns(child, true));
            break;
          case 'br':
            textRuns.push({ text: '\n', bold: false });
            break;
          case 'ul':
          case 'ol':
            hasList = true;
            node.querySelectorAll('li').forEach(li => {
              const liTextRuns = [];
              const extractLiText = (n, bold = false) => {
                if (n.nodeType === Node.TEXT_NODE) {
                  const text = n.textContent?.trim();
                  if (text) {
                    liTextRuns.push({ text: text + ' ', bold });
                  }
                } else if (n.nodeType === Node.ELEMENT_NODE) {
                  const t = n.nodeName.toLowerCase();
                  if (t === 'strong' || t === 'b') {
                    n.childNodes.forEach(c => extractLiText(c, true));
                  } else {
                    n.childNodes.forEach(c => extractLiText(c, bold));
                  }
                }
              };
              li.childNodes.forEach(child => extractLiText(child));
              const liTextParts = [];
              let currentLiRun = null;
              liTextRuns.forEach(tr => {
                if (currentLiRun && currentLiRun.bold === tr.bold) {
                  currentLiRun.text += tr.text;
                } else {
                  if (currentLiRun) {
                    liTextParts.push(currentLiRun);
                  }
                  currentLiRun = { ...tr };
                }
              });
              if (currentLiRun) {
                liTextParts.push(currentLiRun);
              }
              const hasBold = liTextParts.some(tr => tr.bold);
              const liText = liTextParts.map(tr => tr.text).join('').trim();
              if (liText) {
                listChildren.push({ text: liText, bold: hasBold });
              }
            });
            break;
          case 'span':
          case 'div':
          case 'p':
            node.childNodes.forEach(child => extractTextRuns(child, isBold));
            break;
          default:
            const text = node.textContent?.trim();
            if (text) {
              textRuns.push({ text: text + ' ', bold: isBold });
            }
        }
      }
    };
    
    element.childNodes.forEach(child => extractTextRuns(child));
    
    const mergedRuns = [];
    let currentRun = null;
    
    textRuns.forEach(run => {
      if (currentRun && currentRun.bold === run.bold) {
        currentRun.text += run.text;
      } else {
        if (currentRun) {
          mergedRuns.push(currentRun);
        }
        currentRun = { ...run };
      }
    });
    if (currentRun) {
      mergedRuns.push(currentRun);
    }
    
    if (hasList && listChildren.length > 0) {
      const combinedText = mergedRuns.map(r => r.text).join('').trim();
      result.push({
        text: combinedText || '',
        bold: false,
        children: listChildren
      });
    } else if (mergedRuns.length > 0) {
      result.push(...mergedRuns);
    }
  };
  
  const topLevelParagraphs = Array.from(doc.body.querySelectorAll('p'));
  if (topLevelParagraphs.length > 0) {
    topLevelParagraphs.forEach(p => {
      const textRunsBeforeList = [];
      const textRunsAfterList = [];
      const listChildren = [];
      let hasList = false;
      let foundList = false;
      
      const paragraphSibling = p.nextSibling;
      let siblingList = null;
      if (paragraphSibling && paragraphSibling.nodeType === Node.ELEMENT_NODE) {
        const siblingTag = paragraphSibling.nodeName.toLowerCase();
        if (siblingTag === 'ul' || siblingTag === 'ol') {
          siblingList = paragraphSibling;
        }
      }
      
      const processNodeInOrder = (node, isBold = false) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (text) {
            const targetArray = foundList ? textRunsAfterList : textRunsBeforeList;
            targetArray.push({ text: text, bold: isBold });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tag = node.nodeName.toLowerCase();
          
          switch (tag) {
            case 'strong':
            case 'b':
              node.childNodes.forEach(child => processNodeInOrder(child, true));
              break;
            case 'br':
              const targetArray = foundList ? textRunsAfterList : textRunsBeforeList;
              targetArray.push({ text: '\n', bold: false });
              break;
            case 'ul':
            case 'ol':
              hasList = true;
              foundList = true;
              const listItems = node.querySelectorAll('li');
              const itemsToProcess = listItems.length > 0 
                ? Array.from(listItems)
                : Array.from(node.children).filter(child => child.nodeName.toLowerCase() === 'li');
              
              itemsToProcess.forEach(li => {
                const liTextRuns = [];
                const extractLiText = (n, bold = false) => {
                  if (n.nodeType === Node.TEXT_NODE) {
                    const text = n.textContent;
                    if (text) {
                      liTextRuns.push({ text: text, bold });
                    }
                  } else if (n.nodeType === Node.ELEMENT_NODE) {
                    const t = n.nodeName.toLowerCase();
                    if (t === 'strong' || t === 'b') {
                      n.childNodes.forEach(c => extractLiText(c, true));
                    } else {
                      n.childNodes.forEach(c => extractLiText(c, bold));
                    }
                  }
                };
                li.childNodes.forEach(child => extractLiText(child));
                const liTextParts = [];
                let currentLiRun = null;
                liTextRuns.forEach(tr => {
                  if (currentLiRun && currentLiRun.bold === tr.bold) {
                    currentLiRun.text += tr.text;
                  } else {
                    if (currentLiRun) {
                      liTextParts.push(currentLiRun);
                    }
                    currentLiRun = { ...tr };
                  }
                });
                if (currentLiRun) {
                  liTextParts.push(currentLiRun);
                }
                const hasBold = liTextParts.some(tr => tr.bold);
                const liText = liTextParts.map(tr => tr.text).join('').trim();
                if (liText) {
                  listChildren.push({ text: liText, bold: hasBold });
                }
              });
              break;
            case 'span':
            case 'div':
              node.childNodes.forEach(child => processNodeInOrder(child, isBold));
              break;
            default:
              // For other tags, process their children
              node.childNodes.forEach(child => processNodeInOrder(child, isBold));
          }
        }
      };
      
      p.childNodes.forEach(child => processNodeInOrder(child));
      
      if (!hasList || listChildren.length === 0) {
        const directList = p.querySelector('ul, ol');
        if (directList) {
          hasList = true;
          foundList = true;
          directList.querySelectorAll('li').forEach(li => {
            const liTextRuns = [];
            const extractLiText = (n, bold = false) => {
              if (n.nodeType === Node.TEXT_NODE) {
                const text = n.textContent;
                if (text) {
                  liTextRuns.push({ text: text, bold });
                }
              } else if (n.nodeType === Node.ELEMENT_NODE) {
                const t = n.nodeName.toLowerCase();
                if (t === 'strong' || t === 'b') {
                  n.childNodes.forEach(c => extractLiText(c, true));
                } else {
                  n.childNodes.forEach(c => extractLiText(c, bold));
                }
              }
            };
            li.childNodes.forEach(child => extractLiText(child));
            const liTextParts = [];
            let currentLiRun = null;
            liTextRuns.forEach(tr => {
              if (currentLiRun && currentLiRun.bold === tr.bold) {
                currentLiRun.text += tr.text;
              } else {
                if (currentLiRun) {
                  liTextParts.push(currentLiRun);
                }
                currentLiRun = { ...tr };
              }
            });
            if (currentLiRun) {
              liTextParts.push(currentLiRun);
            }
            const hasBold = liTextParts.some(tr => tr.bold);
            const liText = liTextParts.map(tr => tr.text).join('').trim();
            if (liText) {
              listChildren.push({ text: liText, bold: hasBold });
            }
          });
        }
      }
      
      if ((!hasList || listChildren.length === 0) && siblingList) {
        hasList = true;
        foundList = true;
        siblingList.querySelectorAll('li').forEach(li => {
          const liText = li.textContent?.trim() || '';
          if (liText) {
            const hasBold = li.querySelector('strong, b') !== null;
            listChildren.push({ text: liText, bold: hasBold });
          }
        });
      }
      
      const mergeRuns = (runs) => {
        const merged = [];
        let currentRun = null;
        runs.forEach(run => {
          if (currentRun && currentRun.bold === run.bold) {
            currentRun.text += run.text;
          } else {
            if (currentRun) {
              merged.push(currentRun);
            }
            currentRun = { ...run };
          }
        });
        if (currentRun) {
          merged.push(currentRun);
        }
        return merged;
      };
      
      const mergedBefore = mergeRuns(textRunsBeforeList);
      const mergedAfter = mergeRuns(textRunsAfterList);
      
      if (hasList && listChildren.length > 0) {
        const beforeText = mergedBefore.map(r => r.text).join('').trim();
        const afterText = mergedAfter.map(r => r.text).join('').trim();
        const parentText = (beforeText + (afterText ? ' ' + afterText : '')).trim();
        
        result.push({
          text: parentText || '',
          bold: false,
          children: listChildren
        });
      } else if (mergedBefore.length > 0 || mergedAfter.length > 0) {
        result.push(...mergedBefore, ...mergedAfter);
      }
    });
  } else {
    const textRunsBeforeList = [];
    const textRunsAfterList = [];
    const listChildren = [];
    let hasList = false;
    let foundList = false;
    
    const processBodyNode = (node, isBold = false) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text) {
          const targetArray = foundList ? textRunsAfterList : textRunsBeforeList;
          targetArray.push({ text: text, bold: isBold });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.nodeName.toLowerCase();
        
        switch (tag) {
          case 'strong':
          case 'b':
            node.childNodes.forEach(child => processBodyNode(child, true));
            break;
          case 'br':
            const targetArray = foundList ? textRunsAfterList : textRunsBeforeList;
            targetArray.push({ text: '\n', bold: false });
            break;
          case 'ul':
          case 'ol':
            hasList = true;
            foundList = true;
            node.querySelectorAll('li').forEach(li => {
              const liTextRuns = [];
              const extractLiText = (n, bold = false) => {
                if (n.nodeType === Node.TEXT_NODE) {
                  const text = n.textContent;
                  if (text) {
                    liTextRuns.push({ text: text, bold });
                  }
                } else if (n.nodeType === Node.ELEMENT_NODE) {
                  const t = n.nodeName.toLowerCase();
                  if (t === 'strong' || t === 'b') {
                    n.childNodes.forEach(c => extractLiText(c, true));
                  } else {
                    n.childNodes.forEach(c => extractLiText(c, bold));
                  }
                }
              };
              li.childNodes.forEach(child => extractLiText(child));
              const liTextParts = [];
              let currentLiRun = null;
              liTextRuns.forEach(tr => {
                if (currentLiRun && currentLiRun.bold === tr.bold) {
                  currentLiRun.text += tr.text;
                } else {
                  if (currentLiRun) {
                    liTextParts.push(currentLiRun);
                  }
                  currentLiRun = { ...tr };
                }
              });
              if (currentLiRun) {
                liTextParts.push(currentLiRun);
              }
              const hasBold = liTextParts.some(tr => tr.bold);
              const liText = liTextParts.map(tr => tr.text).join('').trim();
              if (liText) {
                listChildren.push({ text: liText, bold: hasBold });
              }
            });
            break;
          case 'span':
          case 'div':
          case 'p':
            node.childNodes.forEach(child => processBodyNode(child, isBold));
            break;
          default:
            node.childNodes.forEach(child => processBodyNode(child, isBold));
        }
      }
    };
    
    doc.body.childNodes.forEach(node => processBodyNode(node));
    
    const mergeRuns = (runs) => {
      const merged = [];
      let currentRun = null;
      runs.forEach(run => {
        if (currentRun && currentRun.bold === run.bold) {
          currentRun.text += run.text;
        } else {
          if (currentRun) {
            merged.push(currentRun);
          }
          currentRun = { ...run };
        }
      });
      if (currentRun) {
        merged.push(currentRun);
      }
      return merged;
    };
    
    const mergedBefore = mergeRuns(textRunsBeforeList);
    const mergedAfter = mergeRuns(textRunsAfterList);
    
    if (hasList && listChildren.length > 0) {
      const beforeText = mergedBefore.map(r => r.text).join('').trim();
      const afterText = mergedAfter.map(r => r.text).join('').trim();
      const parentText = (beforeText + (afterText ? ' ' + afterText : '')).trim();
      
      result.push({
        text: parentText || '',
        bold: false,
        children: listChildren
      });
    } else if (mergedBefore.length > 0 || mergedAfter.length > 0) {
      result.push(...mergedBefore, ...mergedAfter);
    } else {
      doc.body.childNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          processElement(node);
        }
      });
    }
  }
  
  if (result.length === 0) {
    const plainText = (
      doc.body?.textContent ||
      doc.documentElement?.textContent ||
      ''
    ).trim();
    return plainText ? [{ text: plainText, bold: false }] : [{ text: htmlString, bold: false }];
  }
  
  return result.map(item => {
    if (typeof item === 'string') {
      return { text: item, bold: false };
    }
    if (item && typeof item === 'object') {
      return {
        text: item.text || '',
        bold: item.bold || false,
        children: item.children || undefined
      };
    }
    return { text: String(item), bold: false };
  });
}

function getBase64FromUrl(url, callback) {
  const xhr = new XMLHttpRequest();
  xhr.onload = function () {
    const reader = new FileReader();
    reader.onloadend = function () {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };
  xhr.open('GET', url);
  xhr.responseType = 'blob';
  xhr.send();
}

function makeSchemaData(schemaType) {
  for (const generated of this.genPageSchemaTypes) {
    if (generated.type === schemaType) {
      return JSON.parse(JSON.stringify(generated.schema, null, 4));
    }
  }
  return '';
}

let linksCountSignal = [
  'Total Internal Links',
  'Internal links without 404',
  'Internal Broken links',
]

const jsonContent = `{
  "@context": "https://schema.org",
  "@type": "Article",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "add URL"
  },
  "headline": "add headline",
  "image": "add image URL",
  "datePublished": "add date",
  "author": {
    "@type": "Person",
    "name": "add author name"
  },
  "publisher": {
    "@type": "Organization",
    "name": "add organization name",
    "logo": {
      "@type": "ImageObject",
      "url": "add image url"
    }
  },
  "description": "add description"
}`;

const defaultSchemas = [
  {
    type: "Article schema",
    schema: jsonContent
  }
];

export default {
  // preferEditedDrafts: POP Authority deliverables; the draft written in the content editor is the deliverable,
  // so exports use it whenever it exists, whatever the White Glove "replace report" toggle says
  props: ['siloKeyword', 'silo', 'isWhiteGloveUser', 'isMissionControlSession', 'isOrderedReport', 'orderedReportType',
    'preferEditedDrafts'],
  data() {
    return {
      menuOpen: false,
      wordDocHeadImg: null,
      bulbIcon: null,
      eeatWorldIcon: null,
      pageIcon: null,
      checkMarkIcon: null,
      greenInfoIcon: null,
      mainKeywordIcon: null,
      wordCountIcon: null,
      eeatKeywordWarningIcon: null,
      eeatKeywordPresentIcon: null,
      eeatReportLogo: null,
      schemasReportLogo: null,
      descriptionObj: {},
    }
  },
  computed: {
    siloReportProgress() {
      return this.$store.getters.siloReportProgress;
    },
    isSelectionDisabled() {
      return !!this.siloReportProgress || !keywordHasGuardDiffData(this.siloKeyword);
    },
    isExistingReportVisible() {
      return keywordHasGuardDiffData(this.siloKeyword)
    },
    /** Export the content editor's version of each draft (the toggle, or a POP Authority deliverable that has one). */
    useEditorAiDraft() {
      const k = this.siloKeyword
      return !!(k.editedReports.replaceAiDraftReport || (this.preferEditedDrafts && k.hasEditedSource.hasAiDraft))
    },
    useEditorHumanDraft() {
      const k = this.siloKeyword
      return !!(k.editedReports.replaceHumanDraftReport || (this.preferEditedDrafts && k.hasEditedSource.hasHumanDraft))
    },
    useEditorOutline() {
      const k = this.siloKeyword
      return !!(k.editedReports.replaceOutlineReport || (this.preferEditedDrafts && k.hasEditedSource.hasOutline))
    },
    isAIDraftReportVisible() {
      return  this.orderedReportType === 1 & (this.siloKeyword.aiRunResult.aiGenDraft || this.siloKeyword.editedReports.replaceAiDraftReport || this.siloKeyword.hasEditedSource.hasAiDraft)
    },
    isHumanDraftVisible() {
      return this.orderedReportType === 2 & (this.siloKeyword.aiRunResult.aiGenDraft || this.siloKeyword.editedReports.replaceHumanDraftReport || this.siloKeyword.hasEditedSource.hasHumanDraft)
    },
    isAIOutlineVisible() {
      return this.orderedReportType === 3 & (this.siloKeyword.aiRunResult.aiOutline || this.siloKeyword.editedReports.replaceOutlineReport || this.siloKeyword.hasEditedSource.hasOutline)
    },
    isTopLevelKeyword() {
      return this.siloKeyword.keywordData.isTopLevelKeyword
    },
    isSupportingKeyword() {
      return this.siloKeyword.keywordData.isSupportingKeyword
    },
    /** The panel rows in order: an item shows for both page types or for the one it is flagged with. */
    visibleMenuItems() {
      const items = this.getMenuItems().filter((item) => item.isVisible && (
        item.isAvailableToBoth
        // eslint-disable-next-line eqeqeq
        || item.isTopLevelKeyword == this.isTopLevelKeyword
        // eslint-disable-next-line eqeqeq
        || item.isSupportingKeyword == this.isSupportingKeyword
      ))
      // the first row never opens with a divider line
      return items.map((item, i) => (i === 0 && item.divider ? { ...item, divider: false } : item))
    },
    customLogo() {
      return this.$store.state.user.customLogo
    },
    pageType() {
      return this.siloKeyword.keywordData.isTopLevelKeyword ? "top_level_page" : "supporting_page"
    },
    isPageRun() {
      return this.siloKeyword.pageRunId
    },
    isEATTAvailable() {
      return this.siloKeyword.pageRunResult.isEEATAvailable
    },
    areSchemasAvailable() {
      return this.siloKeyword.pageRunResult.isSchemaAvailable
    },
    selectedSubAccountRelation() {
      return this.$store.state.selectedSubAccountRelation
    },
    aiWriterLink() {
      let runId = this.siloKeyword.pageRunId;
      if (!runId) return;
      let url = `/#/content-editor?pageRunId=${runId}&editorMode=AI_MODE&selectTargetWC=true`
      if (this.siloKeyword.id) {
        url += `&siloKeywordId=${this.siloKeyword.id}`
      }
      if (this.isWhiteGloveUser || this.isMissionControlSession) {
        url += `&isWhiteGloveUser=true`
      }
      url += `&isOrderedReport=${this.isOrderedReport ? 'true' : 'false'}`
      if (this.orderedReportType) {
        url += `&orderedReportType=${this.orderedReportType}`
      }
      if (this?.silo?.id) {
        url += `&siloId=${this?.silo?.id}`
      }
      if (this.selectedSubAccountRelation) {
        url += `&relId=${this.selectedSubAccountRelation.id}`
      }
      return url
    },
    contentEditorLink() {
      let secretKey = this.siloKeyword.pageRunResult.secretKey;
      if (!secretKey) return;
      let url = `/#/content-editor?secretKey=${secretKey}&siloKeywordId=${this.siloKeyword.id}`
      if (this.selectedSubAccountRelation) {
        url += `&relId=${this.selectedSubAccountRelation.id}`
      }
      console.log('this.isOrderedReport', this.isOrderedReport)
      console.log('this.isWhiteGloveUser', this.isWhiteGloveUser)
      console.log('this.isMissionControlSession', this.isMissionControlSession)
      console.log('this.orderedReportType', this.orderedReportType)
      if (this.isOrderedReport && (this.isWhiteGloveUser || this.isMissionControlSession) && this.orderedReportType) {
        url += `&isWhiteGloveUser=true&isOrderedReport=true&orderedReportType=${this.orderedReportType}`
      }
      return url
    }
  },
  methods: {
    mainPageSchemaTypes(data) {
      return data.eatSchemaTypes || []
    },
    getHostName(url) {
      if (url.indexOf("//") > -1) {
        return url.split("/")[2];
      } else {
        return url.split("/")[0];
      }
    },
    extractHostnameWithProtocol(url) {
      let hostname = this.getHostName(url);
      if (!url.includes(hostname)) hostname = this.getHostName(url); // this validated domain is different to user inputed, then we will use user inputted
      if (url.toLowerCase().startsWith("https")) {
        return "https://" + hostname;
      } else {
        return "http://" + hostname;
      }
    },
    reportConditions(reportType = "top_level") {
      const { siloKeyword, siloReportProgress, isTopLevelKeyword } = this;
      if (!siloKeyword) {
        return true;
      }
      const isTopLevel = this.isTopLevelKeyword;
      if (isTopLevelKeyword) {
        if (reportType === "top_level") {
          return (siloReportProgress || !keywordHasGuardDiffData(siloKeyword)) && isTopLevel;
        } else {
          return !siloKeyword.aiRunResult.aiRunData;
        }
      } else {
        return !siloKeyword.aiRunResult.aiRunData;
      }
    },
    gotoAiRun() {
      let url = this.aiWriterLink
      if (!url) return

      window
        .open(
          this.aiWriterLink,
          '_blank',
        )
        .focus();
    },
    gotoContentEditor() {
      let url = this.contentEditorLink;
      if (!url) return;


      window
        .open(
          url,
          '_blank',
        )
        .focus();
    },
    gotoChecklistEditor() {
      this.$router.push({ name: 'ChecklistEditorDetail', params: { siloKeywordId: this.siloKeyword.id } });
    },
    getMenuItems() {
      return [
        /*{
          title: `Create AI Writer Outline`,
          class: "mainColor3--text font-weight-bold",
          icon: 'list',
          onClick: "gotoAiRun",
          isDisabled: false,
          isTopLevelKeyword: null,
          isAvailableToBoth: true,
          isVisible: this.isPageRun && (this.isWhiteGloveUser && this.isMissionControlSession),
        },*/
        {
          title: `Create AI Writer article`,
          class: "mainColor3--text font-weight-bold",
          onClick: "gotoAiRun",
          vIcon: "mdi-creation",
          isDisabled: false,
          isTopLevelKeyword: null,
          isAvailableToBoth: true,
          isVisible: this.isPageRun && (this.isWhiteGloveUser && this.isMissionControlSession),
        },
        {
          title: `Open Content Editor`,
          class: "mainColor3--text font-weight-bold",
          vIcon: "mdi-open-in-new",
          onClick: "gotoContentEditor",
          isDisabled: false,
          isTopLevelKeyword: null,
          isAvailableToBoth: true,
          isVisible: this.isPageRun && (this.isWhiteGloveUser && this.isMissionControlSession),
        },
        {
          title: `Open Checklist Editor`,
          class: "mainColor3--text font-weight-bold",
          vIcon: "mdi-open-in-new",
          onClick: "gotoChecklistEditor",
          isDisabled: false,
          isTopLevelKeyword: null,
          isAvailableToBoth: true,
          isVisible: this.isMissionControlSession && this.isTopLevelKeyword && !this.reportConditions(),
        },
        {
          title: `Download EEAT report`,
          children: [
            { title: 'PDF', onClick: 'generateEEATPDF' },
            { title: 'Word', onClick: 'createEEATDocx' },
            { title: 'PDF - Without instructions', onClick: 'generateEEATPDF', params: [true] },
            { title: 'Word - Without instructions', onClick: 'createEEATDocx', params: [true] }
          ],
          divider: true,
          isDisabled: false,
          isTopLevelKeyword: null,
          isAvailableToBoth: true,
          isVisible: this.isWhiteGloveUser && this.isEATTAvailable,
        },
        {
          // design label; the page run has its own schema, so the report comes with Word exports
          title: `Download Schema`,
          children: [
            { title: 'PDF', onClick: 'generateSchemasPDF' },
            { title: 'PDF - Without instructions', onClick: 'generateSchemasPDF', params: [true] },
            { title: 'Word', onClick: 'getSchemasWord', params:[true] },
            { title: 'Word - Without instructions', onClick: 'getSchemasWord', params: [false] },
            { title: 'JSON', onClick: 'generateSchemasJSONReport' },
            // { title: 'JSON - Without instructions', onClick: 'generateSchemasJSONReport', params: [true] },
            
          ],
          divider: !this.isEATTAvailable,
          isDisabled: false,
          isTopLevelKeyword: null,
          isAvailableToBoth: true,
          isVisible: this.isWhiteGloveUser && this.areSchemasAvailable,
        },
        {
          title: `Download Schema`,
          children: [
            { title: 'PDF', onClick: 'generateDefaultSchemasPDF' },
            { title: 'PDF - Without instructions', onClick: 'generateDefaultSchemasPDF', params: [true] },
            { title: 'JSON', onClick: 'generateDefaultSchemasJSONReport' },
          ],
          divider: !this.isEATTAvailable,
          isDisabled: false,
          isSupportingKeyword: this.isSupportingKeyword,
          isAvailableToBoth: false,
          isVisible: this.isWhiteGloveUser && !this.areSchemasAvailable,
        },
        {
          title: `Download ${this.isTopLevelKeyword ? 'top-level' : 'supporting'} pages`,
          heading: true,
          divider: true,
          isDisabled: false,
          isTopLevelKeyword: this.isTopLevelKeyword,
          isAvailableToBoth: false,
          isVisible: this.isWhiteGloveUser && (this.isAIDraftReportVisible || this.isHumanDraftVisible || this.isExistingReportVisible || this.isAIOutlineVisible),
        },
        {
          title: `New ${this.isTopLevelKeyword ? 'top-level' : 'supporting'} page draft (AI generated)`,
          link: '#',
          child: true,
          children: [
            { title: 'PDF', onClick: "generateAIDraftPDF" },
            { title: 'Word', onClick: "generateAIPageDraftWord" },
            { title: 'PDF - Without instructions', onClick: "generateAIDraftPDF", params: [true] },
            { title: 'Word - Without instructions', onClick: "generateAIPageDraftWord", params: [true] },
            { title: 'ZIP (.html + images)', onClick: "generateAIDraftZip" },
          ],
          isVisible: this.isWhiteGloveUser && this.isAIDraftReportVisible,
          isTopLevelKeyword: this.isTopLevelKeyword,
          isAvailableToBoth: false,
          isDisabled: false,
        },
        {
          title: `New ${this.isTopLevelKeyword ? 'top-level' : 'supporting'} page draft (human edited)`,
          link: '#',
          child: true,
          children: [
            { title: 'PDF', onClick: "generateHumanEditedPDF" },
            { title: 'Word', onClick: "generateEditedPageDraftWord" },
            { title: 'PDF - Without instructions', onClick: "generateHumanEditedPDF", params: [true] },
            { title: 'Word - Without instructions', onClick: "generateEditedPageDraftWord", params: [true] },
            { title: 'ZIP (.html + images)', onClick: "generateHumanEditedZip" },
          ],
          isVisible: this.isWhiteGloveUser && this.isHumanDraftVisible,
          isTopLevelKeyword: this.isTopLevelKeyword,
          isAvailableToBoth: false,
          isDisabled: false
        },
        {
          title: "Existing top-level page upgrade",
          link: "#",
          child: true,
          children: [
            { title: "PDF", onClick: "generateTopLevelPDFBriefReport" },
            { title: "Word", onClick: "generateTopLevelWordBriefReport" },
            { title: "PDF - Without instructions", onClick: "generateTopLevelPDFBriefReport", params: [true] },
            { title: "Word - Without instructions", onClick: "generateTopLevelWordBriefReport", params: [true] },
          ],
          isDisabled: this.isSelectionDisabled,
          isTopLevelKeyword: true,
          isAvailableToBoth: false,
          isVisible: this.isWhiteGloveUser && this.isExistingReportVisible && !this.siloKeyword?.pageRunResult?.pageNotBuilt,
        },
        {
          title: `New ${this.isTopLevelKeyword ? 'top-level' : 'supporting'} page outline`,
          link: '#',
          child: true,
          children: [
            { title: 'PDF', onClick: "generateOutlinePDF" },
            { title: 'Word', onClick: "generateOutlineWord" },
            { title: 'PDF - Without instructions', onClick: "generateOutlinePDF", params: [true] },
            { title: 'Word - Without instructions', onClick: "generateOutlineWord", params: [true] },
            { title: 'ZIP (.html + images)', onClick: "generateOutlineZip" },
          ],
          isVisible: this.isWhiteGloveUser && this.isAIOutlineVisible,
          isTopLevelKeyword: this.isTopLevelKeyword,
          isAvailableToBoth: false,
          isDisabled: false
        },
      ]
    },
    getReportData(siloId, reportType) {
      if (!siloId) return Promise.resolve(null);
      this.$store.commit('showLoading');
      return this.$store
        .dispatch('getSiloWordReport', { siloKeywordId: siloId, reportType: reportType })
        .then((response) => {
          return response.data
        }).finally(() => {
          this.$store.commit('hideLoading');
        })
    },
    generateTopLevelWordBriefReport: function (ommitInstructions = false) {
      if (this.reportConditions("top_level")) {
        return;
      }
      this.getReportData(this.siloKeyword.id, "top_level").then((responseData) => {
        if (responseData) {
          if (!responseData) return

          // Create a new document
          const doc = new Document({
            styles: {
              default: {
                document: {
                  run: {font: "Inter", size: 24},
                  paragraph: {spacing: {line: 1.40 * 240}}
                }
              },
              paragraphStyles: [
                {
                  name: 'Normal',
                  paragraph: {spacing: {line: 1.40 * 240}}
                },
              ],
            },
            sections: [
              {
                properties: {
                    page: {
                        margin: {
                            top: '40pt',
                            right: '40pt',
                            bottom: '40pt',
                            left: '40pt',
                        },
                    },
                },
                children: [
                  new Paragraph({
                    children: [
                      new ImageRun({
                        data: responseData.headerImg,
                        transformation: getDocxFullWidthImageTransformation(),
                      }),
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: {
                      line: 60,
                    },
                  }),
                  new Paragraph({children: [new TextRun({text: "  "})]}),
                  new Paragraph({children: [new TextRun({text: "  "})]}),
                  ...!ommitInstructions
                      ? [
                        new Table({
                          rows: [
                            new TableRow({
                              children: [
                                new TableCell({
                                  children: [
                                    new Paragraph({
                                      children: [
                                        new TextRun({
                                          text: "Description",
                                          color: "#FFFFFF",
                                          bold: true,
                                          size: 36,
                                        }),
                                      ],
                                      heading: HeadingLevel.HEADING_1,
                                      spacing: {
                                        before: 80,
                                        after: 80,
                                        afterAutoSpacing: true,
                                      },
                                    }),
                                  ],
                                  width: {
                                    size: 100,
                                    type: WidthType.PERCENTAGE,
                                  },
                                  verticalAlign: "center",
                                  shading: {
                                    fill: "#093464",
                                  },
                                  margins: {
                                    top: convertInchesToTwip(0.15),
                                    bottom: convertInchesToTwip(0.1),
                                    left: convertInchesToTwip(0.35),
                                    right: convertInchesToTwip(0.35),
                                  },
                                  borders: {
                                    top: {
                                      color: "#093464",
                                      size: 5,
                                    },
                                    bottom: {
                                      style: BorderStyle.SINGLE,
                                      color: "#093464",
                                      size: 5,
                                    },
                                    left: {
                                      style: BorderStyle.SINGLE,
                                      color: "#093464",
                                      size: 5,
                                    },
                                    right: {
                                      style: BorderStyle.SINGLE,
                                      color: "#093464",
                                      size: 5,
                                    },
                                  },
                                }),
                              ],
                            }),
                            new TableRow({
                              children: [
                                new TableCell({
                                  children: [
                                    new Paragraph({
                                      children: [
                                        new TextRun({
                                          text: "This",
                                        }),
                                        new TextRun({
                                          text: " Existing Page Upgrade Brief ",
                                          bold: true,
                                        }),
                                        new TextRun({
                                          text: "is designed to improve the SEO of an already existing web page on your site. It’s built for non-SEO’s so can be given to your SEO assistant or your client to work their way through and take that underperforming page to a winning page in no time.",
                                        }),
                                      ],
                                      spacing: {line: 1.40 * 240}
                                    }),
                                  ],
                                  width: {
                                    size: 100,
                                    type: WidthType.PERCENTAGE,
                                  },
                                  verticalAlign: "",
                                  shading: {
                                    fill: "#F0F6FF",
                                  },
                                  margins: {
                                    top: convertInchesToTwip(0.35),
                                    left: convertInchesToTwip(0.35),
                                    bottom: convertInchesToTwip(0.35),
                                    right: convertInchesToTwip(0.35),
                                  },
                                  borders: {
                                    top: {
                                      color: "#093464",
                                      size: 5,
                                    },
                                    bottom: {
                                      style: BorderStyle.SINGLE,
                                      color: "#093464",
                                      size: 5,
                                    },
                                    left: {
                                      style: BorderStyle.SINGLE,
                                      color: "#093464",
                                      size: 5,
                                    },
                                    right: {
                                      style: BorderStyle.SINGLE,
                                      color: "#093464",
                                      size: 5,
                                    },
                                  },
                                }),
                              ],
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
                        }),
                      ] : [],
                  new Paragraph({children: [new TextRun({text: "  "})]}),
                  new Paragraph({children: [new TextRun({text: "  "})]}),
                  new Table({
                    rows: [
                      new TableRow({
                        children: [
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Key Info",
                                    color: "#FFFFFF",
                                    bold: true,
                                    size: 36,
                                  }),
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: {
                                  before: 80,
                                  after: 80,
                                  afterAutoSpacing: true,
                                },
                              }),
                            ],
                            width: {
                              size: 100,
                              type: WidthType.PERCENTAGE,
                            },
                            verticalAlign: "center",
                            shading: {
                              fill: "#093464",
                            },
                            margins: {
                              top: convertInchesToTwip(0.15),
                              bottom: convertInchesToTwip(0.1),
                              left: convertInchesToTwip(0.35),
                              right: convertInchesToTwip(0.35),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                size: 5,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                            },
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: " ",
                                    color: "#FFFFFF",
                                    bold: true,
                                    size: 36,
                                  }),
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: {
                                  before: 80,
                                  after: 80,
                                  afterAutoSpacing: true,
                                },
                              }),
                            ],
                            width: {
                              size: 100,
                              type: WidthType.PERCENTAGE,
                            },
                            verticalAlign: "center",
                            shading: {
                              fill: "#093464",
                            },
                            margins: {
                              top: convertInchesToTwip(0.15),
                              bottom: convertInchesToTwip(0.1),
                              left: convertInchesToTwip(0.35),
                              right: convertInchesToTwip(0.35),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                size: 5,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                            },
                          }),
                        ],
                      }),
                      new TableRow({
                        children: [
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Main Keyword:",
                                    size: 22,
                                    bold: true,
                                  }),
                                ],
                              }),
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: responseData.data.mainKeyword,
                                    size: 22,
                                  }),
                                ],
                              }),
                            ],
                            width: {
                              size: 50,
                              type: WidthType.PERCENTAGE,
                            },
                            verticalAlign: "center",
                            shading: {
                              fill: "#F0F6FF",
                            },
                            margins: {
                              top: convertInchesToTwip(0.2),
                              bottom: convertInchesToTwip(0.2),
                              left: convertInchesToTwip(0.35),
                              right: convertInchesToTwip(0.35),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                size: 10,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                            },
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Current word count: ",
                                    size: 22,
                                    bold: true,
                                  }),
                                  new TextRun({
                                    text: responseData.data.currentWC.toLocaleString(),
                                    size: 22,
                                  }),
                                ],
                              }),
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Target word count: ",
                                    size: 22,
                                    bold: true,
                                  }),
                                  new TextRun({
                                    text: responseData.data.recommendedWC.toLocaleString(),
                                    size: 22,
                                  }),
                                ],
                              }),
                            ],
                            width: {
                              size: 50,
                              type: WidthType.PERCENTAGE,
                            },
                            verticalAlign: "center",
                            shading: {
                              fill: "#F0F6FF",
                            },
                            margins: {
                              top: convertInchesToTwip(0.2),
                              bottom: convertInchesToTwip(0.2),
                              left: convertInchesToTwip(0.35),
                              right: convertInchesToTwip(0.35),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                size: 10,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                            },
                          }),
                        ],
                      }),
                      new TableRow({
                        children: [
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Page optimization score:",
                                    size: 22,
                                    bold: true,
                                  }),
                                ],
                                spacing: {
                                  after: 60,
                                }
                              }),
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: responseData.data?.pageRunResult?.pageRunScore ? String(responseData.data?.pageRunResult?.pageRunScore) : '-',
                                    size: 26,
                                    bold: true,
                                    color: responseData.data?.pageRunResult?.pageRunScore ? pageScoreTintColor(responseData.data?.pageRunResult?.pageRunScore || 0) : '#000000'
                                  }),
                                ],
                              }),
                            ],
                            width: {
                              size: 50,
                              type: WidthType.PERCENTAGE,
                            },
                            verticalAlign: "center",
                            shading: {
                              fill: "#F0F6FF",
                            },
                            margins: {
                              top: convertInchesToTwip(0.2),
                              bottom: convertInchesToTwip(0.2),
                              left: convertInchesToTwip(0.35),
                              right: convertInchesToTwip(0.35),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                size: 10,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                            },
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Page URL:",
                                    size: 22,
                                    bold: true,
                                  }),
                                ],
                              }),
                              new Paragraph({
                                children: [
                                  responseData.data.pageURL
                                    ? new ExternalHyperlink({
                                        children: [
                                          new TextRun({
                                            text: responseData.data.pageURL,
                                            style: "Hyperlink",
                                            bold: true,
                                            color: "#0062FF",
                                            size: 20
                                          }),
                                        ],
                                        link: responseData.data.pageURL,
                                      })
                                    : new TextRun({
                                      text: '-',
                                      size: 22,
                                    }),
                                ],
                                spacing: {
                                  line: 1.15 * 240
                                },
                              }),
                            ],
                            width: {
                              size: 50,
                              type: WidthType.PERCENTAGE,
                            },
                            verticalAlign: "center",
                            shading: {
                              fill: "#F0F6FF",
                            },
                            margins: {
                              top: convertInchesToTwip(0.2),
                              bottom: convertInchesToTwip(0.2),
                              left: convertInchesToTwip(0.35),
                              right: convertInchesToTwip(0.35),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                size: 10,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 10,
                              },
                            },
                          }),
                        ],
                      }),
                    ],
                    width: {
                      size: 100,
                      type: WidthType.PERCENTAGE,
                    },
                    columnWidths: DOCX_SCHEMA_INFO_COLUMN_WIDTHS,
                  }),

                  ...responseData?.data?.top_level_report_feedback
                    ? [
                      new Paragraph({children: [new TextRun({text: "  "})]}),
                      new Paragraph({children: [new TextRun({text: "  "})]}),
                      new Table({
                        rows: [
                          new TableRow({
                            children: [
                              new TableCell({
                                children: [
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: "Page Feedback",
                                        color: "#FFFFFF",
                                        bold: true,
                                        size: 36,
                                      }),
                                    ],
                                    heading: HeadingLevel.HEADING_1,
                                    spacing: {
                                      before: 80,
                                      after: 80,
                                      afterAutoSpacing: true,
                                    },
                                  }),
                                ],
                                width: {
                                  size: 100,
                                  type: WidthType.PERCENTAGE,
                                },
                                verticalAlign: "center",
                                shading: {
                                  fill: "#093464",
                                },
                                margins: {
                                  top: convertInchesToTwip(0.15),
                                  bottom: convertInchesToTwip(0.1),
                                  left: convertInchesToTwip(0.35),
                                  right: convertInchesToTwip(0.35),
                                },
                                borders: {
                                  top: {
                                    color: "#093464",
                                    size: 5,
                                  },
                                  bottom: {
                                    style: BorderStyle.SINGLE,
                                    color: "#093464",
                                    size: 5,
                                  },
                                  left: {
                                    style: BorderStyle.SINGLE,
                                    color: "#093464",
                                    size: 5,
                                  },
                                  right: {
                                    style: BorderStyle.SINGLE,
                                    color: "#093464",
                                    size: 5,
                                  },
                                },
                              }),
                            ],
                          }),
                          new TableRow({
                            children: [
                              new TableCell({
                                children: [
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: responseData.data.top_level_report_feedback,
                                      }),
                                    ],
                                    spacing: {line: 1.40 * 240, after: 240}
                                  }),
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: 'Check the following sections for more details.',
                                      }),
                                    ],
                                    spacing: {line: 1.40 * 240}
                                  }),
                                ],
                                width: {
                                  size: 100,
                                  type: WidthType.PERCENTAGE,
                                },
                                verticalAlign: "",
                                shading: {
                                  fill: "#F0F6FF",
                                },
                                margins: {
                                  top: convertInchesToTwip(0.35),
                                  left: convertInchesToTwip(0.35),
                                  bottom: convertInchesToTwip(0.35),
                                  right: convertInchesToTwip(0.35),
                                },
                                borders: {
                                  top: {
                                    color: "#093464",
                                    size: 5,
                                  },
                                  bottom: {
                                    style: BorderStyle.SINGLE,
                                    color: "#093464",
                                    size: 5,
                                  },
                                  left: {
                                    style: BorderStyle.SINGLE,
                                    color: "#093464",
                                    size: 5,
                                  },
                                  right: {
                                    style: BorderStyle.SINGLE,
                                    color: "#093464",
                                    size: 5,
                                  },
                                },
                              }),
                            ],
                          }),
                        ],
                        width: {
                          size: 100,
                          type: WidthType.PERCENTAGE,
                        },
                        columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
                      }),
                    ] : [],
              
              ...(
                responseData.data?.faq_data?.faqs?.length
                || (responseData.data.outline_data && Object.keys(responseData.data.outline_data).length > 0 && responseData.data.outline_data.result?.outline?.sequence?.length)
              ) ? [
                new Paragraph({children: [new TextRun({text: "  "})]}),
                new Paragraph({children: [new TextRun({text: "  "})]}),

                new Table({
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "Page Level SEO Work",
                                  color: "#FFFFFF",
                                  bold: true,
                                  size: 36,
                                }),
                              ],
                              heading: HeadingLevel.HEADING_1,
                              spacing: {
                                before: 80,
                                after: 80,
                                afterAutoSpacing: true,
                              },
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#093464",
                          },
                          margins: {
                            top: convertInchesToTwip(0.15),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            
                            ...(
                              responseData.data.outline_data && Object.keys(responseData.data.outline_data).length > 0 && responseData.data.outline_data.result?.outline?.sequence?.length
                            ) ? [

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "New paragraphs",
                                      color: "#093464",
                                      size: 26,
                                      bold: true,
                                      /*shading: {
                                        fill: "#093464",
                                        type: ShadingType.CLEAR,
                                        color: "auto",
                                      },
                                      border: { style: BorderStyle.SINGLE, size: 40, color: "#093464" },*/
                                    }),
                                  ],
                                  spacing: {
                                    line: 2 * 240,
                                    after: 120,
                                  },
                                }),

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Here’s some suggestions for new paragraphs if you need to increase word count (these are already SEO optimized):",
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                    after: 320
                                  }
                                }),

                                new Table({
                                  columnWidths: DOCX_SINGLE_COLUMN_WIDTHS,
                                  rows: [
                                    new TableRow({
                                      children: [
                                        new TableCell({
                                          children: [
                                            ...(responseData.data.outline_data?.result?.outline?.sequence || []).map((item) => {
                                              const level = item.level;
                                              const text = item.text;
                                              const builtin = item.builtin?.length > 0 ? item.builtin.join(', ') : "N/A";
                                              const lsi = item.lsi?.length > 0 ? item.lsi.join(', ') : "";
                                              const kwLsi = `${builtin} / ${lsi || "N/A"}`.replace(/ \/ N\/A$/, "");

                                              const headingLevel = parseInt(level.replace('H', ''), 10);
                                              const indentLevel = Math.max(0, headingLevel - 2);
                                              const leftMargin = convertInchesToTwip(0.15 + indentLevel * 0.15);

                                              return new Paragraph({
                                                children: [
                                                  new TextRun({
                                                    text: `${level}`,
                                                    size: 20,
                                                    bold: true,
                                                    color: '#FFFFFF',
                                                    shading: {
                                                      fill: '#8A9AAB',
                                                      type: ShadingType.CLEAR,
                                                      color: 'auto',
                                                    }
                                                  }),
                                                  new TextRun({
                                                    text: ' ',
                                                    size: 22
                                                  }),
                                                  new TextRun({
                                                    text: text,
                                                    size: 22,
                                                    bold: level === 'H2'
                                                  })
                                                ],
                                                ...level === 'H3' ?{
                                                  indent: {
                                                    left: convertInchesToTwip(0.25),
                                                  }
                                                } : {},
                                                spacing: {
                                                  before: level === 'H2' ? 240 : 0,
                                                  after: 160,
                                                }
                                              })
                                            }),  
                                          

                                          ],
                                          shading: {
                                            fill: "#FFFFFF",
                                          },
                                          margins: {
                                            top: convertInchesToTwip(0.4),
                                            bottom: convertInchesToTwip(0.4),
                                            left: convertInchesToTwip(0.4),
                                            right: convertInchesToTwip(0.4),
                                          },
                                          borders: {
                                            top: {style: BorderStyle.NONE},
                                            bottom: {style: BorderStyle.NONE},
                                            left: {style: BorderStyle.NONE},
                                            right: {style: BorderStyle.NONE},
                                          }
                                        })
                                      ]
                                    })
                                  ]
                                }),

                                new Paragraph({children: [new TextRun({text: "  "})]}),
                                new Paragraph({children: [new TextRun({text: "  "})]}),

                            ] : [],

                            ...responseData.data?.faq_data?.faqs?.length
                              ? [
                              
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "FAQ suggestions",
                                      color: "#093464",
                                      size: 26,
                                      bold: true,
                                      /*shading: {
                                        fill: "#093464",
                                        type: ShadingType.CLEAR,
                                        color: "auto",
                                      },
                                      border: { style: BorderStyle.SINGLE, size: 40, color: "#093464" },*/
                                    }),
                                  ],
                                  spacing: {
                                    line: 2 * 240,
                                    after: 120,
                                  },
                                }),
                              
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Adding these FAQs to your page will help your content to be included in 'Featured Snippets, People Also Ask & LLMs:",
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                    after: 320
                                  }
                                }),

                                ...responseData.data.faq_data.faqs.map((faq) => [
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: faq.question,
                                        bold: true,
                                      }),
                                    ],
                                    spacing: {
                                      line: 1.40 * 240,
                                    },
                                    indent: {
                                      left: convertInchesToTwip(0.25),
                                    },
                                  }),
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: faq.answer,
                                      }),
                                    ],
                                    spacing: {
                                      line: 1.40 * 240,
                                      after: 240
                                    },
                                    indent: {
                                      left: convertInchesToTwip(0.25),
                                    },
                                  }),
                                ]).flat(),
                              ] : [],
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "",
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.35),
                            left: convertInchesToTwip(0.35),
                            bottom: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
                }),
              ] : [],

              ...(
                responseData.data?.recommend_title?.recommendation
                || responseData.data?.subHeadingsAddAll?.length
                || (
                  responseData.data?.is_overoptimized && (
                    responseData.data?.keywordSuggestions?.reduce?.subHeadings?.length
                    || responseData.data?.keywordSuggestions?.remove?.subHeadings?.length
                  )
                )
                || responseData.data?.old_related_searches
                || responseData.data?.new_related_searches
              ) ? [
                new Paragraph({children: [new TextRun({text: "  "})]}),
                new Paragraph({children: [new TextRun({text: "  "})]}),

                new Table({
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "SEO Word Work",
                                  color: "#FFFFFF",
                                  bold: true,
                                  size: 36,
                                }),
                              ],
                              heading: HeadingLevel.HEADING_1,
                              spacing: {
                                before: 80,
                                after: 80,
                                afterAutoSpacing: true,
                              },
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#093464",
                          },
                          margins: {
                            top: convertInchesToTwip(0.15),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [

                            ...(
                              responseData.data?.recommend_title?.recommendation
                            ) ? [

                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Improve your Title & H1 Tags",
                                    color: "#093464",
                                    size: 26,
                                    bold: true,
                                  }),
                                ],
                                spacing: {
                                  line: 2 * 240,
                                  after: 240,
                                },
                              }),

                              ...(
                                responseData.data?.current_title
                                || responseData.data?.current_h1
                              ) ? [

                                responseData.data?.current_title && new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Current Title Tag: ${responseData.data?.current_title}`,
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                  }
                                }),

                                responseData.data?.current_h1 && new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Current H1: ${responseData.data?.current_h1}`,
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                  }
                                }),

                                new Paragraph({children: [new TextRun({text: "  "})]}),
                              ] : [],

                              ...(
                                responseData.data?.recommend_title?.recommendation
                              ) ? [

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Consider these alternative Title Tags & H1s (each are SEO optimized):`,
                                      bold: true
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                  }
                                }),

                                ...[
                                  responseData.data?.recommend_title?.recommendation?.draft_title,
                                  ...(responseData.data?.recommend_title?.ranked_alternatives || []).map(alt => alt.draft_title)
                                ].map(draft_title => (
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: draft_title.replace('2025', '2026'),
                                      }),
                                    ],
                                    bullet: {
                                      level: 0,
                                    },
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  })
                                )),

                                new Paragraph({children: [new TextRun({text: "  "})]}),
                              ] : [],

                              ...(
                                responseData.data?.keywordSuggestions?.add?.title?.length
                                || responseData.data?.keywordSuggestions?.add?.pageTitle?.length
                              ) ? [

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Whichever Title Tag & H1 you go with, make sure it includes these words:`,
                                      bold: true
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                  }
                                }),

                                ...[
                                  ...(responseData.data?.keywordSuggestions?.add?.title || []),
                                  ...(responseData.data?.keywordSuggestions?.add?.pageTitle || []).filter(item => !(responseData.data?.keywordSuggestions?.add?.title || []).map(x => x.keyword).includes(item.keyword))
                                ].map(item => (
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `${item.keyword} (Current: ${item.current}${item.target?'; Target: ' + String(item.target) : '' })`,
                                      }),
                                    ],
                                    bullet: {
                                      level: 0,
                                    },
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  })
                                )),
                              
                                new Paragraph({children: [new TextRun({text: "  "})]}),
                              ] : [],

                              ...(
                                  responseData.data?.is_overoptimized && responseData.data?.titleReduceAll?.length
                              ) ? [

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Decrease or remove these Variation, LSI and NLP terms:`,
                                      bold: true
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                  }
                                }),

                                ...(responseData.data?.titleReduceAll || []).map(item => (
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `${item.keyword} (Current: ${item.current}${item.targetMin === 0||item.targetMax === 0||item.targetMin||item.targetMax?'; Target: ' + String(item.targetMin) + ' - ' + String(item.targetMax) : '' })`,
                                      }),
                                    ],
                                    bullet: {
                                      level: 0,
                                    },
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  })
                                )),

                                new Paragraph({children: [new TextRun({text: "  "})]}),
                              ] : [],
                            ] : [],

                            ...(
                              responseData.data?.subHeadingsAddAll?.length
                              || (responseData.data?.is_overoptimized && responseData.data?.subHeadingsReduceAll?.length)
                              || (
                                responseData.data?.is_overoptimized && (
                                  responseData.data?.keywordSuggestions?.reduce?.subHeadings?.length
                                  || responseData.data?.keywordSuggestions?.remove?.subHeadings?.length
                                )
                              )
                            ) ? [
                            
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Improve your SubHeadings (H2 & H3 Tags)",
                                    color: "#093464",
                                    size: 26,
                                    bold: true,
                                  }),
                                ],
                                spacing: {
                                  line: 2 * 240,
                                  after: 240,
                                },
                              }),

                              ...(
                                responseData.data?.subHeadingsAddAll?.length
                              ) ? [

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Increase suggested Variation, LSI and NLP terms:`,
                                      bold: true
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                  }
                                }),

                                ...(responseData.data?.subHeadingsAddAll || []).map(item => (
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `${item.keyword} (Current: ${item.current}${item.targetMin||item.targetMax?'; Target: ' + String(item.targetMin) + ' - ' + String(item.targetMax) : '' }${item.target?'; Target: ' + String(item.target) : '' })`,
                                      }),
                                    ],
                                    bullet: {
                                      level: 0,
                                    },
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  })
                                )),

                                new Paragraph({children: [new TextRun({text: "  "})]}),
                              ] : [],

                              ...responseData.data?.is_overoptimized ? [

                                ...(
                                  responseData.data?.subHeadingsReduceAll?.length
                                ) ? [

                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `Decrease or remove these Variation, LSI and NLP terms:`,
                                        bold: true
                                      }),
                                    ],
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  }),

                                  ...(responseData.data?.subHeadingsReduceAll || []).map(item => (
                                    new Paragraph({
                                      children: [
                                        new TextRun({
                                          text: `${item.keyword} (Current: ${item.current}${item.targetMin === 0||item.targetMax === 0||item.targetMin||item.targetMax?'; Target: ' + String(item.targetMin) + ' - ' + String(item.targetMax) : '' })`,
                                        }),
                                      ],
                                      bullet: {
                                        level: 0,
                                      },
                                      spacing: {
                                        line: 1.40 * 240,
                                      }
                                    })
                                  )),

                                  new Paragraph({children: [new TextRun({text: "  "})]}),
                                ] : [],

                              ] : [],

                            ] : [],

                            ...(
                              responseData.data?.old_related_searches
                              || responseData.data?.new_related_searches
                              || responseData.data?.pAddAll?.length
                              || (responseData.data?.is_overoptimized && responseData.data?.pReduceAll?.length)
                            ) ? [
                            
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: "Improve your Paragraph text",
                                    color: "#093464",
                                    size: 26,
                                    bold: true,
                                  }),
                                ],
                                spacing: {
                                  line: 2 * 240,
                                  after: 240,
                                },
                              }),

                              ...(
                                responseData.data?.pAddAll?.length
                              ) ? [

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Increase suggested Variation, LSI and NLP terms:`,
                                      bold: true
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                  }
                                }),

                                ...(responseData.data?.pAddAll || []).map(item => (
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `${item.keyword} (Current: ${item.current}${item.targetMin||item.targetMax?'; Target: ' + String(item.targetMin) + ' - ' + String(item.targetMax) : '' })`,
                                      }),
                                    ],
                                    bullet: {
                                      level: 0,
                                    },
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  })
                                )),

                                new Paragraph({children: [new TextRun({text: "  "})]}),
                              ] : [],

                              ...responseData.data?.is_overoptimized ? [

                                ...(
                                  responseData.data?.pReduceAll?.length
                                ) ? [

                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `Decrease or remove these Variation, LSI and NLP terms:`,
                                        bold: true
                                      }),
                                    ],
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  }),

                                  ...(responseData.data?.pReduceAll || []).map(item => (
                                    new Paragraph({
                                      children: [
                                        new TextRun({
                                          text: `${item.keyword} (Current: ${item.current}${item.targetMin === 0||item.targetMax === 0||item.targetMin||item.targetMax?'; Target: ' + String(item.targetMin) + ' - ' + String(item.targetMax) : '' })`,
                                        }),
                                      ],
                                      bullet: {
                                        level: 0,
                                      },
                                      spacing: {
                                        line: 1.40 * 240,
                                      }
                                    })
                                  )),

                                  new Paragraph({children: [new TextRun({text: "  "})]}),
                                ] : [],

                              ] : [],

                              ...(
                                responseData.data?.old_related_searches
                                || responseData.data?.new_related_searches
                              ) ? [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Include these related keywords into existing or new paragraphs:`,
                                      bold: true
                                    }),
                                  ],
                                  spacing: {
                                    line: 1.40 * 240,
                                    after: 120,
                                  }
                                }),

                                ...[
                                  ...(responseData.data?.old_related_searches || []),
                                  ...(responseData.data?.new_related_searches || []),
                                ].map(item => (
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: item.query,
                                      }),
                                    ],
                                    bullet: {
                                      level: 0,
                                    },
                                    spacing: {
                                      line: 1.40 * 240,
                                    }
                                  })
                                )),

                                new Paragraph({children: [new TextRun({text: "  "})]}),
                              ] : [],

                            ] : [],

                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "",
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.35),
                            left: convertInchesToTwip(0.35),
                            bottom: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
                }),
              ] : [],

              ...(
                responseData.data?.schema_data?.length
              ) ? [
                new Paragraph({children: [new TextRun({text: "  "})]}),
                new Paragraph({children: [new TextRun({text: "  "})]}),
                
                new Table({
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "Page Schema",
                                  color: "#FFFFFF",
                                  bold: true,
                                  size: 36,
                                }),
                              ],
                              heading: HeadingLevel.HEADING_1,
                              spacing: {
                                before: 80,
                                after: 80,
                                afterAutoSpacing: true,
                              },
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#093464",
                          },
                          margins: {
                            top: convertInchesToTwip(0.15),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "This is important for search engines and LLM to categorize your page and site contextually. We found these schemas particularly relevant to this page, so add these into your page header.",
                                }),
                              ],
                              spacing: {
                                line: 1.40 * 240,
                                after: 360,
                              }
                            }),
                            new Table({
                              columnWidths: DOCX_SCHEMA_INFO_COLUMN_WIDTHS,
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              rows: [
                                ...responseData.data.schema_data.map(item => (
                                  new TableRow({
                                    children: [
                                      new TableCell({
                                        children: [
                                          new Paragraph({
                                            children: [
                                              new TextRun({
                                                text: item.type,
                                                bold: true,
                                              }),
                                            ],
                                            spacing: {line: 1.40 * 240}
                                          }),
                                        ],
                                        margins: {
                                          top: convertInchesToTwip(0.12),
                                          left: convertInchesToTwip(0.12),
                                          bottom: convertInchesToTwip(0.12),
                                          right: convertInchesToTwip(0.12),
                                        },
                                      }),
                                      new TableCell({
                                        children: [
                                          new Paragraph({
                                            children: [
                                              new ExternalHyperlink({
                                                children: [
                                                  new TextRun({
                                                    text: "Download Schema",
                                                    style: "Hyperlink",
                                                    bold: true,
                                                    color: "#0062FF",
                                                  }),
                                                ],
                                                link: `${ responseData.data.page_run_url }?tab=schemaType&scroll_to_schema=true&generate=${item.type}`,
                                              }),
                                            ],
                                            spacing: {line: 1.40 * 240}
                                          }),
                                        ],
                                        margins: {
                                          top: convertInchesToTwip(0.12),
                                          left: convertInchesToTwip(0.12),
                                          bottom: convertInchesToTwip(0.12),
                                          right: convertInchesToTwip(0.12),
                                        },
                                      }),
                                    ]
                                  })
                                ))
                              ],
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "",
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.35),
                            left: convertInchesToTwip(0.35),
                            bottom: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new ImageRun({
                                  data: Uint8Array.from(
                                    atob(this.greenInfoIcon.split(",")[1]),
                                    (c) => c.charCodeAt(0)
                                  ),
                                  transformation: { width: 18, height: 18 },
                                  floating: {
                                      horizontalPosition: {
                                          relative: HorizontalPositionRelativeFrom.CHARACTER,
                                          offset: 0,
                                      },
                                      verticalPosition: {
                                          relative: VerticalPositionRelativeFrom.PARAGRAPH,
                                          offset: 0,
                                      },
                                      wrap: {
                                          type: TextWrappingType.SQUARE,
                                          side: TextWrappingSide.BOTH_SIDES,
                                      },
                                      allowOverlap: false,
                                  },
                                }),
                                new TextRun({
                                  text: " Check out ",
                                  bold: true,
                                }),
                                new ExternalHyperlink({
                                  children: [
                                    new TextRun({
                                      text: "this article in our help center",
                                      style: "Hyperlink",
                                      bold: true,
                                      color: "#0062FF",
                                    }),
                                  ],
                                  link: "https://help.pageoptimizer.pro/article/how-to-add-pageoptimizer-pros-schema-to-your-pages",
                                }),
                                new TextRun({
                                  text: " for tips on how to download schema and implement it in different CMS and page builders.",
                                  bold: true,
                                }),
                              ],
                              spacing: {line: 1.40 * 240}
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "",
                          shading: {
                            fill: "#E8FFF5",
                          },
                          margins: {
                            top: convertInchesToTwip(0.2),
                            left: convertInchesToTwip(0.2),
                            bottom: convertInchesToTwip(0.2),
                            right: convertInchesToTwip(0.2),
                          },
                          borders: {
                            top: {
                              color: "#25A97A",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
                }),
              ] : [],

              ...(
                responseData?.data?.eatSchemaTypes?.length
              ) ? [
                new Paragraph({children: [new TextRun({text: "  "})]}),
                new Paragraph({children: [new TextRun({text: "  "})]}),
                
                new Table({
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "EEAT signals",
                                  color: "#FFFFFF",
                                  bold: true,
                                  size: 36,
                                }),
                              ],
                              heading: HeadingLevel.HEADING_1,
                              spacing: {
                                before: 80,
                                after: 80,
                                afterAutoSpacing: true,
                              },
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#093464",
                          },
                          margins: {
                            top: convertInchesToTwip(0.15),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "EEAT helps Google assess a site's Trust and Authority. Add these ",
                                }),
                                new TextRun({
                                  text: "missing page elements",
                                  bold: true,
                                }),
                                new TextRun({
                                  text: " to improve Trust & Authority:",
                                }),
                              ],
                              spacing: {
                                line: 1.40 * 240,
                                after: 360,
                              }
                            }),
                            new Table({
                              columnWidths: DOCX_SCHEMA_TYPE_COLUMN_WIDTHS,
                              rows: [
                                ...responseData.data.eatSchemaTypes.map(item => (
                                  new TableRow({
                                    children: [
                                      new TableCell({
                                        children: [
                                          new Paragraph({
                                            children: [
                                              new TextRun({
                                                text: item.signal,
                                                bold: true,
                                                size: 20,
                                              }),
                                            ],
                                            spacing: {line: 1.20 * 240}
                                          }),
                                          new Paragraph({
                                            children: [
                                              new TextRun({
                                                text: 'MISSING',
                                                color: '#FF1B17',
                                                size: 18,
                                              }),
                                            ],
                                            spacing: {line: 1.40 * 240}
                                          }),
                                        ],
                                        margins: {
                                          top: convertInchesToTwip(0.12),
                                          left: convertInchesToTwip(0.12),
                                          bottom: convertInchesToTwip(0.12),
                                          right: convertInchesToTwip(0.12),
                                        },
                                      }),
                                      new TableCell({
                                        children: [
                                          new Paragraph({
                                            children: [
                                              new TextRun({
                                                text: explainData[item.signal] ?? " ",
                                                size: 20,
                                              }),
                                            ],
                                            spacing: {line: 1.40 * 240}
                                          }),
                                        ],
                                        margins: {
                                          top: convertInchesToTwip(0.12),
                                          left: convertInchesToTwip(0.12),
                                          bottom: convertInchesToTwip(0.12),
                                          right: convertInchesToTwip(0.12),
                                        },
                                      }),
                                    ]
                                  })
                                ))
                              ],
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "",
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.35),
                            left: convertInchesToTwip(0.35),
                            bottom: convertInchesToTwip(0.35),
                            right: convertInchesToTwip(0.35),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new ImageRun({
                                  data: Uint8Array.from(
                                    atob(this.greenInfoIcon.split(",")[1]),
                                    (c) => c.charCodeAt(0)
                                  ),
                                  transformation: { width: 18, height: 18 },
                                  floating: {
                                      horizontalPosition: {
                                          relative: HorizontalPositionRelativeFrom.CHARACTER,
                                          offset: 0,
                                      },
                                      verticalPosition: {
                                          relative: VerticalPositionRelativeFrom.PARAGRAPH,
                                          offset: 0,
                                      },
                                      wrap: {
                                          type: TextWrappingType.SQUARE,
                                          side: TextWrappingSide.BOTH_SIDES,
                                      },
                                      allowOverlap: false,
                                  },
                                }),
                                new TextRun({
                                  text: " Check out ",
                                  bold: true,
                                }),
                                new ExternalHyperlink({
                                  children: [
                                    new TextRun({
                                      text: "this article in our help center",
                                      style: "Hyperlink",
                                      bold: true,
                                      color: "#0062FF",
                                    }),
                                  ],
                                  link: "https://help.pageoptimizer.pro/article/what-eeat-signals-look-like",
                                }),
                                new TextRun({
                                  text: " to learn some examples showcasing what EEAT signals look like when present on your page.",
                                  bold: true,
                                }),
                              ],
                              spacing: {line: 1.40 * 240}
                            }),
                          ],
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "",
                          shading: {
                            fill: "#E8FFF5",
                          },
                          margins: {
                            top: convertInchesToTwip(0.2),
                            left: convertInchesToTwip(0.2),
                            bottom: convertInchesToTwip(0.2),
                            right: convertInchesToTwip(0.2),
                          },
                          borders: {
                            top: {
                              color: "#25A97A",
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#25A97A",
                              size: 5,
                            },
                          },
                        }),
                      ],
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
                }),
              ] : [],
              
              new Paragraph({children: [new TextRun({text: "  "})]}),
              new Paragraph({children: [new TextRun({text: "  "})]}),

              new Table({
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "Checking your work",
                                color: "#FFFFFF",
                                bold: true,
                                size: 36,
                              }),
                              new TextRun({
                                text: "  ",
                                color: "#FFFFFF",
                                bold: true,
                                size: 36,
                              }),
                              new ImageRun({
                                data: Uint8Array.from(
                                  atob(this.checkMarkIcon.split(",")[1]),
                                  (c) => c.charCodeAt(0)
                                ),
                                transformation: { width: 22, height: 22 },
                              }),
                            ],
                            heading: HeadingLevel.HEADING_1,
                            spacing: {
                              before: 80,
                              after: 80,
                              afterAutoSpacing: true,
                            },
                          }),
                        ],
                        width: {
                          size: 100,
                          type: WidthType.PERCENTAGE,
                        },
                        verticalAlign: "center",
                        shading: {
                          fill: "#093464",
                        },
                        margins: {
                          top: convertInchesToTwip(0.15),
                          bottom: convertInchesToTwip(0.1),
                          left: convertInchesToTwip(0.35),
                          right: convertInchesToTwip(0.35),
                        },
                        borders: {
                          top: {
                            color: "#093464",
                            size: 5,
                          },
                          bottom: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                          left: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                          right: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                        },
                      }),
                    ],
                  }),
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "When you are happy with your improvements it's a good idea to check your work. At the top of the brief you will see your previous Page Optimization Score (out of 100). Hopefully, if you have implemented our suggestions your page will now score 100 (or at least above 80). This means you’ve done everything you can, from an on-page SEO perspective",
                              }),
                            ],
                            spacing: {
                              after: 240,
                              line: 1.40 * 240
                            },
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "Here is how to check your updated Page Optimization Score:",
                              }),
                            ],
                            spacing: {
                              line: 1.40 * 240
                            },
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "1. Download the POP Chrome Extension ",
                              }),
                              new ExternalHyperlink({
                                children: [
                                  new TextRun({
                                    text: "here",
                                    style: "Hyperlink",
                                    bold: true,
                                    color: "#0062FF",
                                  }),
                                ],
                                link: "https://chromewebstore.google.com/detail/pageoptimizer-pro/nfpngpgicoflhemmfebjahmjfcbnfobi?hl=en&pli=1",
                              }),
                              new TextRun({
                                text: " (Free)",
                              }),
                            ],
                            spacing: {line: 1.40 * 240}
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "2. Follow this ",
                              }),
                              new ExternalHyperlink({
                                children: [
                                  new TextRun({
                                    text: "tutorial",
                                    style: "Hyperlink",
                                    bold: true,
                                    color: "#0062FF",
                                  }),
                                ],
                                link: "https://help.pageoptimizer.pro/article/pageoptimizer-pro-chrome-extension-guide",
                              }),
                            ],
                            spacing: {
                              after: 240,
                              line: 1.40 * 240
                            },
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "Here is a list of POP Chrome Extension ",
                              }),
                              new ExternalHyperlink({
                                children: [
                                  new TextRun({
                                    text: "compatible editors",
                                    style: "Hyperlink",
                                    bold: true,
                                    color: "#0062FF",
                                  }),
                                ],
                                link: "https://help.pageoptimizer.pro/article/compatible-editors",
                              }),
                              new TextRun({
                                text: ".",
                              }),
                            ],
                            spacing: {line: 1.40 * 240}
                          }),
                        ],
                        width: {
                          size: 100,
                          type: WidthType.PERCENTAGE,
                        },
                        verticalAlign: "",
                        shading: {
                          fill: "#F0F6FF",
                        },
                        margins: {
                          top: convertInchesToTwip(0.35),
                          left: convertInchesToTwip(0.35),
                          bottom: convertInchesToTwip(0.35),
                          right: convertInchesToTwip(0.35),
                        },
                        borders: {
                          top: {
                            color: "#093464",
                            size: 5,
                          },
                          bottom: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                          left: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                          right: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                        },
                      }),
                    ],
                  }),
                ],
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
                columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
              }),
                ],
              },
            ],
          });

          const timestamp = moment().format("YYYY_M_D_h_mm_ss");
          const fileName = `existing_page_update_brief_keyword_${responseData.data.mainKeyword.replace(" ", "_")}_${timestamp}.docx`;
          downloadWordFile(doc, fileName)
        }
      });
    },
    // Top Level Outline
    generateOutlineWord(ommitInstructions = false) {
      if (!this.siloKeyword.aiRunResult.aiOutline && !this.siloKeyword.hasEditedSource.hasOutline) {
        return
      }

      if (this.useEditorOutline) {
        downloadEditorReport(this.$store, ReportTypes.OUTLINE, "word", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
        return
      }
      if (this.reportConditions("outline")) {
        return;
      }
      this.getReportData(this.siloKeyword.id, "outline").then((responseData) => {

        const doc = new Document({
          styles: { default: { document: { run: { font: "Arial" } } } },
          sections: [
            {
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: responseData.headerImg,
                      transformation: getDocxFullWidthImageTransformation(),
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: {
                    line: 240,
                    after: 480,
                  },
                }),
                ...!ommitInstructions
                  ?[
                    new Table({
                      rows: [
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "💡 Instructions",
                                      color: "#FFFFFF",
                                      bold: true,
                                      size: 38,
                                    }),
                                  ],
                                  heading: HeadingLevel.HEADING_1,
                                  alignment: AlignmentType.CENTER,
                                  spacing: {
                                    before: 120,
                                    after: 120,
                                    afterAutoSpacing: true,
                                  },
                                }),
                              ],
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              verticalAlign: "center",
                              shading: {
                                fill: "#093464",
                              },
                              margins: {
                                top: convertInchesToTwip(0.1),
                                bottom: convertInchesToTwip(0.1),
                                left: convertInchesToTwip(0.2),
                                right: convertInchesToTwip(0.2),
                              },
                              borders: {
                                top: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                bottom: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                left: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                right: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                              },
                            }),
                          ],
                        }),
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Use your favorite AI or human writing tool to draft the content for this page based on the",
                                    }),
                                    new TextRun({
                                      text: " instructions ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "below.",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children:
                                    this.siloKeyword.keywordData.isTopLevelKeyword ?
                                      [new TextRun({
                                        text: "When satisfied with the outcome, you may want to do a final SEO check of your final draft using the",
                                      }),
                                      new TextRun({
                                        text: " POP Chrome Extension ",
                                        bold: true,
                                      }),
                                      new TextRun({
                                        text: "to ensure the content scores above 80.",
                                        bold: true,
                                      }),]
                                      :
                                      [new TextRun({
                                        text: "When satisfied with the outcome, just do a final check",
                                      }),
                                      new TextRun({
                                        text: " final check ",
                                        bold: true,
                                      }),
                                      new TextRun({
                                        text: "to ensure the main keyword is included at least once in these places:",
                                        bold: true,
                                      }),]
                                  ,
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 120,
                                    after: 120,
                                  },
                                }),
                                ...this.siloKeyword.keywordData.isTopLevelKeyword ?
                                  [
                                    new Paragraph({
                                      children: [
                                        new ExternalHyperlink({
                                          children: [
                                            new TextRun({
                                              text: "Here is a tutorial explaining how to do that.",
                                              style: "Hyperlink",
                                              bold: true,
                                              color: "#FF5700",
                                            }),
                                          ],
                                          link: "https://help.pageoptimizer.pro/article/pageoptimizer-pro-chrome-extension-guide",
                                        }),
                                      ],
                                      bullet: {
                                        level: 1,
                                      },
                                      spacing: {
                                        before: 120,
                                        after: 120,
                                      },
                                    }),
                                    new Paragraph({
                                      children: [
                                        new ExternalHyperlink({
                                          children: [
                                            new TextRun({
                                              text: "Here is a list of POP Chrome Extension compatible editors.",
                                              style: "Hyperlink",
                                              bold: true,
                                              color: "#FF5700",
                                            }),
                                          ],
                                          link: "https://help.pageoptimizer.pro/article/compatible-editors",
                                        }),
                                      ],
                                      bullet: {
                                        level: 1,
                                      },
                                      spacing: {
                                        before: 120,
                                        after: 120,
                                      },
                                    }),
                                  ].flat()
                                  :
                                  [
                                    new Paragraph({
                                      children: [
                                        new TextRun({
                                          text: "The title tag & H1 tag.",
                                        }),
                                      ],
                                      bullet: {
                                        level: 1,
                                      },
                                      spacing: {
                                        before: 120,
                                        after: 120,
                                      },
                                    }),
                                    new Paragraph({
                                      children: [
                                        new TextRun({
                                          text: "The main content.",
                                        }),
                                      ],
                                      bullet: {
                                        level: 1,
                                      },
                                      spacing: {
                                        before: 120,
                                        after: 120,
                                      },
                                    }),
                                    new Paragraph({
                                      children: [
                                        new TextRun({
                                          text: "If possible, the URL of the page (however do not change an existing URL).",
                                        }),
                                      ],
                                      bullet: {
                                        level: 1,
                                      },
                                      spacing: {
                                        before: 120,
                                        after: 120,
                                      },
                                    })
                                  ].flat()
                                ,

                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Your content is now ready to",
                                    }),
                                    new TextRun({
                                      text: " publish ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "to your website."
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Don't forget to",
                                    }),
                                    new TextRun({
                                      text: " interlink",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: " the page according to the",
                                    }),
                                    new TextRun({
                                      text: " Silo map",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: ` included in the ${responseData.dashboardName || 'POP White Glove'} dashboard.`,
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "For a detailed",
                                    }),
                                    new TextRun({
                                      text: " tutorial",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: " on how the POP on-page process works ",
                                    }),
                                    new ExternalHyperlink({
                                      children: [
                                        new TextRun({
                                          text: "start here",
                                          style: "Hyperlink",
                                          bold: true,
                                          color: "#FF5700",
                                        }),
                                      ],
                                      link: "https://academy.pageoptimizer.pro/lessons/introduction/",
                                    }),
                                    new TextRun({
                                      text: ".",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                              ],
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              verticalAlign: "",
                              shading: {
                                fill: "#F0F6FF",
                              },
                              margins: {
                                top: convertInchesToTwip(0.15),
                                left: convertInchesToTwip(0.15),
                                bottom: convertInchesToTwip(0.15),
                                right: convertInchesToTwip(0.15),
                              },
                              borders: {
                                top: {
                                  color: "#093464",
                                  size: 5,
                                },
                                bottom: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                left: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                right: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                              },
                            }),
                          ],
                        }),
                      ],
                      width: {
                        size: 100,
                        type: WidthType.PERCENTAGE,
                      },
                      columnWidths: DOCX_SINGLE_COLUMN_WIDTHS,
                    }),
                  ]
                  :[],
                new Paragraph({ children: [new TextRun({ text: "  " })] }),
                new Table({
                  rows: [
                    // Header Row
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "PAGE INFORMATION",
                                  color: "#FFFFFF",
                                  bold: true,
                                  size: 28,
                                }),
                              ],
                              alignment: AlignmentType.LEFT,
                              spacing: { before: 50, after: 50 },
                            }),
                          ],
                          columnSpan: 3, // Span all 3 columns
                          width: { size: 100, type: WidthType.PERCENTAGE },
                          verticalAlign: "center",
                          shading: { fill: "#093464" },
                          margins: {
                            top: convertInchesToTwip(0.15),
                            bottom: convertInchesToTwip(0.15),
                            left: convertInchesToTwip(0.2),
                            right: convertInchesToTwip(0.2),
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                        children: [
                          new Table({
                            rows: [
                              new TableRow({
                                children: [
                                  new TableCell({
                                    children: [
                                      new Paragraph({
                                        children: [
                                          new ImageRun({
                                            data: Uint8Array.from(
                                              atob(this.pageIcon.split(",")[1]),
                                              (c) => c.charCodeAt(0)
                                            ),
                                            transformation: { width: 17, height: 20 },
                                          }),
                                        ],
                                        alignment: AlignmentType.LEFT,
                                      }),
                                    ],
                                    verticalAlign: "center",
                                    width: { size: 10, type: "pct" },
                                    borders: { top: { size: 0 }, bottom: { size: 0 }, left: { size: 0 }, right: { size: 0 } },
                                    margins: { right: 15 * 20 },
                                  }),

                                  new TableCell({
                                    children: [
                                      new Paragraph({
                                        children: [
                                          new TextRun({
                                            text: "Type of page:",
                                            bold: true,
                                            size: 20,
                                            color: "#333333",
                                          }),
                                        ],
                                        alignment: AlignmentType.LEFT,
                                      }),
                                      new Paragraph({
                                        children: [
                                          new TextRun({
                                            text: responseData.data.pageType || "Top Level Page",
                                            size: 18,
                                            color: "#000000",
                                          }),
                                        ],
                                        alignment: AlignmentType.LEFT,
                                      }),
                                    ],
                                    verticalAlign: "center",
                                    width: { size: 90, type: "pct" },
                                    borders: { top: { size: 0 }, bottom: { size: 0 }, left: { size: 0 }, right: { size: 0 } },
                                  }),
                                ],
                              }),
                            ],
                            width: { size: 100, type: "pct" },
                            borders: {
                              top: { size: 0 },
                              bottom: { size: 0 },
                              left: { size: 0 },
                              right: { size: 0 },
                              insideHorizontal: { size: 0 },
                              insideVertical: { size: 0 }
                            },
                          }),
                        ],


                          width: { size: 33.33, type: WidthType.PERCENTAGE },
                          verticalAlign: "top",
                          shading: { fill: "#F0F6FF" },
                          margins: {
                            top: convertInchesToTwip(0.12),
                            bottom: convertInchesToTwip(0.12),
                            left: convertInchesToTwip(0.15),
                            right: convertInchesToTwip(0.15),
                          },
                          borders: {
                            top: { style: BorderStyle.SINGLE, color: "#E5E5E5", size: 3 },
                            right: { style: BorderStyle.SINGLE, color: "#E5E5E5", size: 3 },
                          },
                        }),
                        new TableCell({
                         children: [
                            new Table({
                              rows: [
                                new TableRow({
                                  children: [
                                    new TableCell({
                                      children: [
                                        new Paragraph({
                                          children: [
                                            new ImageRun({
                                              data: Uint8Array.from(
                                                atob(this.orangeKeyIcon.split(",")[1]),
                                                (c) => c.charCodeAt(0)
                                              ),
                                              transformation: { width: 24, height: 12 },
                                            }),
                                          ],
                                          alignment: AlignmentType.LEFT,
                                        }),
                                      ],
                                      verticalAlign: "center",
                                      width: { size: 10, type: "pct" },
                                      borders: { top: { size: 0 }, bottom: { size: 0 }, left: { size: 0 }, right: { size: 0 } },
                                      margins: { right: 20 * 20 },
                                    }),

                                    new TableCell({
                                      children: [
                                        new Paragraph({
                                          children: [
                                            new TextRun({
                                              text: "Main Keyword:",
                                              bold: true,
                                              size: 20,
                                              color: "#333333",
                                            }),
                                          ],
                                          alignment: AlignmentType.LEFT,
                                        }),
                                        new Paragraph({
                                          children: [
                                            new TextRun({
                                              text: responseData.data.mainKeyword || "-",
                                              size: 18,
                                              color: "#000000",
                                            }),
                                          ],
                                          alignment: AlignmentType.LEFT,
                                          spacing: {
                                            line: 1.15 * 240
                                          },
                                        }),
                                      ],
                                      verticalAlign: "center",
                                      width: { size: 90, type: "pct" },
                                      borders: { top: { size: 0 }, bottom: { size: 0 }, left: { size: 0 }, right: { size: 0 } },
                                    }),
                                  ],
                                }),
                              ],
                              width: { size: 100, type: "pct" },
                              borders: {
                                top: { size: 0 },
                                bottom: { size: 0 },
                                left: { size: 0 },
                                right: { size: 0 },
                                insideHorizontal: { size: 0 },
                                insideVertical: { size: 0 }
                              },
                            }),
                          ],
                          width: { size: 33.33, type: WidthType.PERCENTAGE },
                          verticalAlign: "top",
                          shading: { fill: "#F0F6FF" },
                          margins: {
                            top: convertInchesToTwip(0.12),
                            bottom: convertInchesToTwip(0.12),
                            left: convertInchesToTwip(0.15),
                            right: convertInchesToTwip(0.15),
                          },
                          borders: {
                            top: { style: BorderStyle.SINGLE, color: "#E5E5E5", size: 3 },
                            left: { style: BorderStyle.SINGLE, color: "#E5E5E5", size: 3 },
                            right: { style: BorderStyle.SINGLE, color: "#E5E5E5", size: 3 },
                          },
                        }),

                        new TableCell({
                          children: [
                            new Table({
                              rows: [
                                new TableRow({
                                  children: [
                                    new TableCell({
                                      children: [
                                        new Paragraph({
                                          children: [
                                            new ImageRun({
                                              data: Uint8Array.from(
                                                atob(this.wordCountIcon.split(",")[1]),
                                                (c) => c.charCodeAt(0)
                                              ),
                                              transformation: { width: 22, height: 15 },
                                            }),
                                          ],
                                          alignment: AlignmentType.LEFT,
                                        }),
                                      ],
                                      verticalAlign: "center",
                                      width: { size: 10, type: "pct" },
                                      borders: { top: { size: 0 }, bottom: { size: 0 }, left: { size: 0 }, right: { size: 0 } },
                                      margins: { right: 20 * 20 },
                                    }),
                                    new TableCell({
                                      children: [
                                        new Paragraph({
                                          children: [
                                            new TextRun({
                                              text: "Recommended word count:",
                                              bold: true,
                                              size: 18,
                                              color: "#333333",
                                            }),
                                          ],
                                          alignment: AlignmentType.LEFT,
                                        }),
                                        new Paragraph({
                                          children: [
                                            new TextRun({
                                              text: `Around ${responseData.data.recommendedWC || "XXXX"} words${
                                                responseData.data.isTopLevelKeyword ? " (give or take 10%)" : ""
                                              }`,
                                              size: 18,
                                              color: "#000000",
                                            }),
                                          ],
                                          alignment: AlignmentType.LEFT,
                                        }),
                                      ],
                                      verticalAlign: "center",
                                      width: { size: 90, type: "pct" },
                                      borders: { top: { size: 0 }, bottom: { size: 0 }, left: { size: 0 }, right: { size: 0 } },
                                    }),
                                  ],
                                }),
                              ],
                              width: { size: 100, type: "pct" },
                              borders: {
                                top: { size: 0 },
                                bottom: { size: 0 },
                                left: { size: 0 },
                                right: { size: 0 },
                                insideHorizontal: { size: 0 },
                                insideVertical: { size: 0 }
                              },
                            }),
                          ],
                          width: { size: 33.33, type: WidthType.PERCENTAGE },
                          verticalAlign: "top",
                          shading: { fill: "#F0F6FF" },
                          margins: {
                            top: convertInchesToTwip(0.12),
                            bottom: convertInchesToTwip(0.12),
                            left: convertInchesToTwip(0.15),
                            right: convertInchesToTwip(0.15),
                          },
                          borders: {
                            top: { style: BorderStyle.SINGLE, color: "#E5E5E5", size: 3 },
                            left: { style: BorderStyle.SINGLE, color: "#E5E5E5", size: 3 },
                          },
                        }),
                      ],
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  layout: TableLayoutType.FIXED,
                }),

                new Paragraph({
                  children: [new TextRun({ text: " " })],
                  spacing: { after: 480 }
                }),

                new Paragraph({
                  children: [new PageBreak()],
                }),
              ],
            },
          ],
        });
        // Outline
        const outlineTable = new Table({
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: "📝 Page Outline",
                          color: "#FFFFFF",
                          bold: true,
                          size: 38,
                        }),
                      ],
                      heading: HeadingLevel.HEADING_1,
                      alignment: AlignmentType.CENTER,
                      spacing: {
                        before: 240,
                        after: 240,
                        afterAutoSpacing: true,
                      },
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  verticalAlign: "center",
                  shading: {
                    fill: "#093464",
                  },
                  margins: {
                    top: convertInchesToTwip(0.1),
                    bottom: convertInchesToTwip(0.1),
                    left: convertInchesToTwip(0.1),
                    right: convertInchesToTwip(0.1),
                  },
                  borders: {
                    top: {
                      color: "#093464",
                      size: 5,
                    },
                    bottom: {
                      style: BorderStyle.SINGLE,
                      color: "#FFFFFF",
                      size: 5,
                    },
                    left: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                    right: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                  },
                }),
              ],
            })
          ],
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          columnWidths: DOCX_SINGLE_COLUMN_WIDTHS,
        })
        // Heading Section
        if (responseData.data.aiRunData.heading) {
          const heading = responseData.data.aiRunData.heading;
          const headingSection = new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `<${heading.tag.toUpperCase()}> `,
                        color: "#000000",
                        size: 28,
                        bold: true,

                      }),
                      new TextRun({
                        text: `${heading.text}`,
                        color: "#000000",
                        bold: true,
                        size: 28,
                      }),
                    ],
                    spacing: {
                      before: 60,
                      after: 60,
                      afterAutoSpacing: true,
                    },
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: 'Note: We recommend your page title tag be the same as your H1.',
                        size: 22,
                        italics: true
                      }),
                    ],
                    spacing: {
                      before: 60,
                      after: 60,
                      afterAutoSpacing: true,
                    },
                  }),
                ],
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
                verticalAlign: "center",
                shading: {
                  fill: "#F0F6FF",
                },
                margins: {
                  top: convertInchesToTwip(0.1),
                  bottom: convertInchesToTwip(0.1),
                  left: convertInchesToTwip(0.1),
                  right: convertInchesToTwip(0.1),
                },
                borders: {
                  top: {
                    style: BorderStyle.SINGLE,
                    color: "#F0F6FF",
                    size: 5,
                  },
                  bottom: {
                    style: BorderStyle.SINGLE,
                    color: "#F0F6FF",
                    size: 5,
                  },
                  left: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                  right: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                },
              }),
            ],
          })
          outlineTable.addChildElement(headingSection)
        }
        // Introduction Section
        if (responseData.data.aiRunData.introduction) {
          const introductionSection = new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Intro",
                        bold: true,
                        size: 28,
                      }),
                    ],
                    spacing: {
                      before: 60,
                      after: 60,
                      afterAutoSpacing: true,
                    },
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        // text: responseData.data.aiRunData.introduction,
                        size: 24,
                      }),
                    ],
                    spacing: {
                      before: 60,
                      after: 60,
                      afterAutoSpacing: true,
                    },
                  }),
                ],
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
                verticalAlign: "center",
                shading: {
                  fill: "#F0F6FF",
                },
                margins: {
                  top: convertInchesToTwip(0.1),
                  bottom: convertInchesToTwip(0.1),
                  left: convertInchesToTwip(0.1),
                  right: convertInchesToTwip(0.1),
                },
                borders: {
                  top: {
                    style: BorderStyle.SINGLE,
                    color: "#F0F6FF",
                    size: 5,
                  },
                  bottom: {
                    style: BorderStyle.SINGLE,
                    color: "#F0F6FF",
                    size: 5,
                  },
                  left: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                  right: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                },
              }),
            ],
          })
          outlineTable.addChildElement(introductionSection)
        }
        // Sub heading section
        const subHeadingSection = new Table({
          rows: [
            ...responseData.data.aiRunData.subHeadingsTree.map(
              (heading) => {
                return [
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: `<${heading.tag.toUpperCase()}> `,
                                color: "#000000",
                                size: 28,
                                bold: true,
                              }),
                              new TextRun({
                                text: `${heading.text}`,
                                color: "#000000",
                                bold: true,
                                size: 28,
                              }),
                            ],
                            spacing: {
                              before: 60,
                              after: 60,
                              afterAutoSpacing: true,
                            },
                          })
                        ],
                        width: {
                          size: 100,
                          type: WidthType.PERCENTAGE,
                        },
                        verticalAlign: "center",
                        shading: {
                          fill: "#F0F6FF",
                        },
                        margins: {
                          top: convertInchesToTwip(0.1),
                          bottom: convertInchesToTwip(0.1),
                          left: convertInchesToTwip(0.1),
                          right: convertInchesToTwip(0.1),
                        },
                        borders: {
                          top: {
                            style: BorderStyle.SINGLE,
                            color: "#F0F6FF",
                            size: 5,
                          },
                          bottom: {
                            style: BorderStyle.SINGLE,
                            color: "#F0F6FF",
                            size: 5,
                          },
                          left: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                          right: {
                            style: BorderStyle.SINGLE,
                            color: "#093464",
                            size: 5,
                          },
                        },
                      }),
                    ],
                  }),
                  ...heading.children.map(
                    (subHeading) => {
                      return [
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `        <${subHeading.tag.toUpperCase()}> `,
                                      size: 24,
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: `${subHeading.text}`,
                                      size: 24,
                                    }),
                                  ],
                                  spacing: {
                                    before: 60,
                                    after: 60,
                                    afterAutoSpacing: true,
                                  },
                                }),
                              ],
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              verticalAlign: "center",
                              shading: {
                                fill: "#F0F6FF",
                              },
                              margins: {
                                top: convertInchesToTwip(0.1),
                                bottom: convertInchesToTwip(0.1),
                                left: convertInchesToTwip(0.1),
                                right: convertInchesToTwip(0.1),
                              },
                              borders: {
                                top: {
                                  style: BorderStyle.SINGLE,
                                  color: "#F0F6FF",
                                  size: 5,
                                },
                                bottom: {
                                  style: BorderStyle.SINGLE,
                                  color: "#F0F6FF",
                                  size: 5,
                                },
                                left: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                right: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                              },
                            }),
                          ],
                        }),
                      ]
                    }
                  ).flat(),
                ]
              }
            ).flat(),
          ],
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          columnWidths: DOCX_LABEL_VALUE_COLUMN_WIDTHS,
        })
        // FAQs
        if (responseData.data.aiRunData.faqData.length > 0) {
          const faqSection = new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "FAQs",
                        bold: true,
                        size: 28,
                      }),
                    ],
                    spacing: {
                      before: 60,
                      after: 60,
                      afterAutoSpacing: true,
                    },
                  }),
                  ...responseData.data.aiRunData.faqData.map(
                    (faq) => {
                      return [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: faq.question,
                              size: 24,
                            }),
                          ],
                          spacing: {
                            before: 60,
                            after: 60,
                            afterAutoSpacing: true,
                          },
                          bullet: {
                            level: 0,
                          },
                        }),
                      ]
                    }
                  ).flat(),
                ],
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
                verticalAlign: "center",
                shading: {
                  fill: "#F0F6FF",
                },
                margins: {
                  top: convertInchesToTwip(0.1),
                  bottom: convertInchesToTwip(0.1),
                  left: convertInchesToTwip(0.1),
                  right: convertInchesToTwip(0.1),
                },
                borders: {
                  top: {
                    style: BorderStyle.SINGLE,
                    color: "#F0F6FF",
                    size: 5,
                  },
                  bottom: {
                    style: BorderStyle.SINGLE,
                    color: "#F0F6FF",
                    size: 5,
                  },
                  left: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                  right: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                },
              }),
            ],
          })

          subHeadingSection.addChildElement(faqSection)
        }

        // Conclusion
        if (responseData.data.aiRunData.conclusion) {
          const conclusionSection =
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: "Conclusion",
                          bold: true,
                          size: 28,
                        }),
                      ],
                      spacing: {
                        before: 60,
                        after: 60,
                        afterAutoSpacing: true,
                      },
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          // text: responseData.data.aiRunData.conclusion,
                          size: 24,
                        }),
                      ],
                      spacing: {
                        before: 60,
                        after: 60,
                        afterAutoSpacing: true,
                      },
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  verticalAlign: "center",
                  shading: {
                    fill: "#F0F6FF",
                  },
                  margins: {
                    top: convertInchesToTwip(0.1),
                    bottom: convertInchesToTwip(0.1),
                    left: convertInchesToTwip(0.1),
                    right: convertInchesToTwip(0.1),
                  },
                  borders: {
                    top: {
                      style: BorderStyle.SINGLE,
                      color: "#F0F6FF",
                      size: 5,
                    },
                    bottom: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                    left: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                    right: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                  },
                }),
              ],
            })

          subHeadingSection.addChildElement(conclusionSection)
        } else {
          // Add closing border to outlineTable
          subHeadingSection.addChildElement(new TableRow({
            children: [
              new TableCell({
                children: [
                ],
                shading: {
                  fill: "#F0F6FF",
                },
                borders: {
                  top: {
                    style: BorderStyle.SINGLE,
                    color: "#F0F6FF",
                    size: 5,
                  },
                  bottom: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                  left: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                  right: {
                    style: BorderStyle.SINGLE,
                    color: "#093464",
                    size: 5,
                  },
                }
              }),
            ],
          })
          )
        }

        // Add outline and subheading section to doc
        doc.addSection({ children: [outlineTable, subHeadingSection] })



        const timestamp = moment().format("YYYY_M_D_h_mm_ss");
        const fileName = `${this.pageType}_outline_keyword_${responseData.data.mainKeyword.replace(" ", "_")}_${timestamp}.docx`;
        downloadWordFile(doc, fileName)
      })
    },
    generateSchemas() {
      this.getReportData(this.siloKeyword.id, "outline").then((responseData) => {

      })
    },
    getSiloPDFReport(siloId, reportType, ommitInstructions = false) {
      if (!siloId) return;
      if (reportType != "schema" && reportType != "eeat" && this.reportConditions(reportType)) {
        return;
      }
      const timestamp = moment().format("YYYY_M_D_h_mm_ss");
      this.$store.commit('showLoading');
      this.$store
        .dispatch('getSiloPDFReport', { siloKeywordId: siloId, reportType: reportType, ommitInstructions })
        .then((response) => {
          const fileName = `${this.pageType}_${reportType}_keyword_${this.siloKeyword.keyword.replace(" ", "_")}_${timestamp}`
          downloadFile(response.data, fileName)
        })
        .finally(() => {
          this.$store.commit('hideLoading');
        });
    },
    // AI and Human Draft Word
    // Word
   generatePageDraftWord(siloId, edited, reportType, ommitInstructions = false) {
      if (this.reportConditions(reportType)) {
        return;
      }
      this.getReportData(siloId, reportType).then((responseData) => {
        // Create a new document
        const doc = new Document({
          styles: { default: { document: { run: { font: "Arial" } } } },
          sections: [
            {
              children: [
                // First Table: One row, two columns
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: responseData.headerImg,
                      transformation: getDocxFullWidthImageTransformation(),
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: {
                    line: 240,
                    after: 480,
                  },
                }),
                ...!ommitInstructions
                  ? [
                    new Table({
                      rows: [
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "💡 Instructions",
                                      color: "#FFFFFF",
                                      bold: true,
                                      size: 38,
                                    }),
                                  ],
                                  heading: HeadingLevel.HEADING_1,
                                  alignment: AlignmentType.CENTER,
                                  spacing: {
                                    before: 120,
                                    after: 120,
                                    afterAutoSpacing: true,
                                  },
                                }),
                              ],
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              verticalAlign: "center",
                              shading: {
                                fill: "#093464",
                              },
                              margins: {
                                top: convertInchesToTwip(0.1),
                                bottom: convertInchesToTwip(0.1),
                                left: convertInchesToTwip(0.2),
                                right: convertInchesToTwip(0.2),
                              },
                              borders: {
                                top: {
                                  color: "#093464",
                                  size: 5,
                                },
                                bottom: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                left: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                right: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                              },
                            }),
                          ],
                        }),
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "From an SEO perspective this is a",
                                    }),
                                    new TextRun({
                                      text: " fully written page draft. ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "The page sections, word count and placement of words have all been",
                                    }),
                                    new TextRun({
                                      text: " optimized for search engines. ",
                                      bold: true,
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Whether this is an",
                                    }),
                                    new TextRun({
                                      text: " AI generated draft ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "or",
                                    }),
                                    new TextRun({
                                      text: " Human edited draft",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: ", (you can confirm by checking the table below in ‘Level of content’) you still need to review and potentially edit the content to ensure it is factually correct, on brand and meets your own content standards.",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 120,
                                    after: 120,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Do keep in mind ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "that if this is Supporting Page content, it is not intended to be super high value, it's intended to meet the needs of search engines first and foremost. If a Supporting Page starts to perform very well it can then be upgraded to a Top Level page and benefit from getting its own Supporting Pages, (no need to worry about this now)",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Have the below content draft",
                                    }),
                                    new TextRun({
                                      text: " reviewed ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "and then publish it to the website.",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "Don't forget to",
                                    }),
                                    new TextRun({
                                      text: " interlink the page according to the Silo map ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: `included in the ${responseData.dashboardName || 'POP White Glove'} dashboard.`,
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "For a detailed",
                                    }),
                                    new TextRun({
                                      text: " tutorial",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: " on how the POP on-page process works ",
                                    }),
                                    new ExternalHyperlink({
                                      children: [
                                        new TextRun({
                                          text: "start here",
                                          style: "Hyperlink",
                                          bold: true,
                                          color: "#FF5700",
                                        }),
                                      ],
                                      link: "https://academy.pageoptimizer.pro/lessons/introduction/",
                                    }),
                                    new TextRun({
                                      text: ".",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                              ],
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              verticalAlign: "",
                              shading: {
                                fill: "#F0F6FF",
                              },
                              margins: {
                                top: convertInchesToTwip(0.15),
                                left: convertInchesToTwip(0.15),
                                bottom: convertInchesToTwip(0.15),
                                right: convertInchesToTwip(0.15),
                              },
                              borders: {
                                top: {
                                  color: "#093464",
                                  size: 5,
                                },
                                bottom: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                left: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                right: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                              },
                            }),
                          ],
                        }),
                      ],
                      width: {
                        size: 100,
                        type: WidthType.PERCENTAGE,
                      },
                      columnWidths: DOCX_SECTION_HEADER_COLUMN_WIDTHS,
                    }),
                  ]
                  : [],
                new Paragraph({ children: [new TextRun({ text: "  " })] }),
                new Table({
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "Type of the Page",
                                  bold: true,
                                  size: 24,
                                  color: "#FFFFFF",
                                }),
                              ],
                              spacing: {
                                before: 60,
                                after: 60,
                                afterAutoSpacing: true,
                              },
                            }),
                          ],
                          width: {
                            size: 50,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#093464",
                          },
                          margins: {
                            top: convertInchesToTwip(0.1),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.1),
                            right: convertInchesToTwip(0.1),
                          },
                          borders: {
                            top: {
                              color: "#FFFFFF",
                              size: 10,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                          },
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: responseData.data.pageType,
                                  size: 22,
                                }),
                              ],
                            }),
                          ],
                          width: {
                            size: 50,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.1),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.1),
                            right: convertInchesToTwip(0.1),
                          },
                          borders: {
                            top: {
                              color: "#FFFFFF",
                              size: 10,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "Level of content",
                                  bold: true,
                                  size: 24,
                                  color: "#FFFFFF",
                                }),
                              ],
                              spacing: {
                                before: 60,
                                after: 60,
                                afterAutoSpacing: true,
                              },
                            }),
                          ],
                          width: {
                            size: 50,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#093464",
                          },
                          margins: {
                            top: convertInchesToTwip(0.1),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.1),
                            right: convertInchesToTwip(0.1),
                          },
                          borders: {
                            top: {
                              color: "#FFFFFF",
                              size: 10,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                          },
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: edited ? "Human edited draft" : "AI generated draft",
                                  size: 22,
                                }),
                              ],
                            }),
                          ],
                          width: {
                            size: 50,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.1),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.1),
                            right: convertInchesToTwip(0.1),
                          },
                          borders: {
                            top: {
                              color: "#FFFFFF",
                              size: 10,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                          },
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "Main Keyword",
                                  bold: true,
                                  size: 24,
                                  color: "#FFFFFF",
                                }),
                              ],
                              spacing: {
                                before: 60,
                                after: 60,
                                afterAutoSpacing: true,
                              },
                            }),
                          ],
                          width: {
                            size: 50,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#093464",
                          },
                          margins: {
                            top: convertInchesToTwip(0.1),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.1),
                            right: convertInchesToTwip(0.1),
                          },
                          borders: {
                            top: {
                              color: "#FFFFFF",
                              size: 10,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                          },
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: responseData.data.mainKeyword,
                                  size: 22,
                                }),
                              ],
                            }),
                          ],
                          width: {
                            size: 50,
                            type: WidthType.PERCENTAGE,
                          },
                          verticalAlign: "center",
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.1),
                            bottom: convertInchesToTwip(0.1),
                            left: convertInchesToTwip(0.1),
                            right: convertInchesToTwip(0.1),
                          },
                          borders: {
                            top: {
                              color: "#FFFFFF",
                              size: 10,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 10,
                            },
                          },
                        }),
                      ],
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  columnWidths: DOCX_LABEL_VALUE_COLUMN_WIDTHS,
                }),
              ],
            },
          ],
        });
        // Page Draft
        const draftTable = new Table({
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: "📝 Page Draft",
                          color: "#FFFFFF",
                          bold: true,
                          size: 38,
                        }),
                      ],
                      heading: HeadingLevel.HEADING_1,
                      alignment: AlignmentType.CENTER,
                      spacing: {
                        before: 240,
                        after: 240,
                        afterAutoSpacing: true,
                      },
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  verticalAlign: "center",
                  shading: {
                    fill: "#093464",
                  },
                  margins: {
                    top: convertInchesToTwip(0.1),
                    bottom: convertInchesToTwip(0.1),
                    left: convertInchesToTwip(0.1),
                    right: convertInchesToTwip(0.1),
                  },
                  borders: {
                    top: {
                      color: "#FFFFFF",
                      size: 5,
                    },
                    bottom: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                    left: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                    right: {
                      style: BorderStyle.SINGLE,
                      color: "#093464",
                      size: 5,
                    },
                  },
                }),
              ],
            }),

          ],
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          columnWidths: DOCX_LABEL_VALUE_COLUMN_WIDTHS,
        })

        const htmlContentRow = new TableRow({
          children: [],
        })

        const htmlContentCell = new TableCell({
          children: [],
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          shading: {
            fill: "#F0F6FF"
          },
          verticalAlign: "center",
          margins: {
            top: convertInchesToTwip(0.2),
            bottom: convertInchesToTwip(0.2),
            left: convertInchesToTwip(0.2),
            right: convertInchesToTwip(0.2),
          },
          borders: {
            top: {
              style: BorderStyle.SINGLE,
              color: "#093464",
              size: 5,
            },
            bottom: {
              style: BorderStyle.SINGLE,
              color: "#093464",
              size: 5,
            },
            left: {
              style: BorderStyle.SINGLE,
              color: "#093464",
              size: 5,
            },
            right: {
              style: BorderStyle.SINGLE,
              color: "#093464",
              size: 5,
            }
          }
        })
        let page_heading;
        let page_introduction;
        if (responseData.data.aiRunData.heading) {
          const page_title = new Paragraph(
            {
              children: [
                new TextRun({
                  text: "PAGE TITLE",
                  bold: true,
                  size: 18,
                  color: "#FF5700"
                })
              ]
            }
          )
          const page_title_content = new Paragraph(
            {
              children: [
                new TextRun({
                  text: `${responseData.data.aiRunData.heading.text}`,
                  bold: true,
                  size: 32,
                })
              ]
            }
          )
          page_heading = new Paragraph(
            {
              children: [
                new TextRun({
                  text: `<${responseData.data.aiRunData.heading.tag.toUpperCase()}>`,
                  size: 32,
                  color: "#FF5700"
                }),
                new TextRun({
                  text: ` ${responseData.data.aiRunData.heading.text}`,
                  bold: true,
                  size: 32
                }),

              ]
            }
          )
          page_introduction = new Paragraph(
            {
              children: [
                new TextRun({
                  text: ` ${responseData.data.aiRunData.introduction}`,
                  bold: false,
                  size: 20
                }),
              ]
            }
          )
          htmlContentCell.addChildElement(page_title)
          htmlContentCell.addChildElement(page_title_content)
          htmlContentCell.addChildElement(new Paragraph({ text: " " }))
        }


        if (responseData.data.aiRunData.metaDescription) {
          const meta_description = new Paragraph(
            {
              children: [
                new TextRun({
                  text: "META DESCRIPTION",
                  bold: true,
                  size: 18,
                  color: "#FF5700"
                })
              ]
            }
          )
          const meta_description_content = new Paragraph(
            {
              children: [
                new TextRun({
                  text: `${responseData.data.aiRunData.metaDescription}`,
                  size: 20,
                })
              ]
            }
          )
          htmlContentCell.addChildElement(meta_description)
          htmlContentCell.addChildElement(meta_description_content)
          htmlContentCell.addChildElement(new Paragraph({ text: " " }))
        }
        htmlContentCell.addChildElement(page_heading)
        htmlContentCell.addChildElement(new Paragraph({ text: " " }))
        htmlContentCell.addChildElement(page_introduction)

          const mainSections = responseData.data.aiRunData.sections.map((section) => {
              if (section.tag === "faq") {
                  const faqContent = parseHtmlData(section.result, "docx").flat();
                  const contentBeforeFaq = [];

                  if (responseData.data.aiRunData.conclusion) {
                      const conclusionSection = new Paragraph({
                          children: [
                              new TextRun({
                                  text: "Conclusion",
                                  bold: true,
                                  size: 28,
                              }),
                          ],
                          spacing: { before: 200, after: 100 },
                      });

                      const conclusionText = new Paragraph({
                          children: [
                              new TextRun({
                                  text: responseData.data.aiRunData.conclusion,
                                  size: 20,
                              }),
                          ],
                          spacing: { before: 60, after: 200 },
                      });

                      contentBeforeFaq.push(conclusionSection, conclusionText);
                  }

                  const formPlaceholder = new Paragraph({
                      children: [
                          new TextRun({
                              text: "<form: your form here>",
                              size: 20,
                          }),
                      ],
                      spacing: { before: 100, after: 100 },
                  });
                  contentBeforeFaq.push(formPlaceholder);

                  return [...contentBeforeFaq, ...faqContent];
              }

              if (section.tag === "table") {
                  return [
                      ...parseHtmlData(section.result, "docx").flat(),
                      new Paragraph({
                          children: [
                              new TextRun({
                                  text: section.tagText || "",
                                  size: 22,
                              }),
                          ],
                          spacing: { before: 200, after: 200 },
                      }),
                  ];
              }

              if (section.tagText === "Key Highlights" && section.result.includes("<ul")) {
                  const liMatches = section.result.match(/<li>(.*?)<\/li>/g) || [];
                  const bulletItems = liMatches.map(li =>
                      li.replace(/<\/?li>/g, "").trim()
                  );

                  return [
                      new Paragraph({
                          children: [
                              new TextRun({
                                  text: `<${section.tag.toUpperCase()}>`,
                                  size: 26,
                                  bold: true,
                                  color: "#FF5700",
                              }),
                              new TextRun({
                                  text: ` ${section.tagText}`,
                                  bold: true,
                                  size: 26,
                              }),
                          ],
                          spacing: { before: 120, after: 60 },
                      }),

                      ...bulletItems.map(item =>
                          new Paragraph({
                              children: [new TextRun({ text: item, size: 20 })],
                              bullet: { level: 0 },
                          })
                      ),
                  ];
              }

              if (section.tag !== "list") {
                  const sectionElements = [];

                  sectionElements.push(
                      new Paragraph({
                          children: [
                              section.tag === "img"
                                  ? new TextRun({ text: `🖼️ <image: your image file link here>`, size: 20 })
                                  : section.tag === "form"
                                  ? new TextRun({ text: "" })
                                  : new TextRun({
                                      children: [
                                          new TextRun({
                                              text: `<${section.tag.toUpperCase()}>`,
                                              bold: true,
                                              size: 26,
                                              color: "#FF5700",
                                          }),
                                          new TextRun({
                                              text: ` ${section.tagText}`,
                                              bold: true,
                                              size: 26,
                                          }),
                                      ],
                                  }),
                          ],
                          spacing: { before: 60, after: 60, afterAutoSpacing: true },
                      })
                  );

                  if (section.tagText === "Brief Overview") {
                      sectionElements.push(
                          new Paragraph({
                              children: [new TextRun({ text: section.result, size: 20 })],
                          })
                      );
                  } else {
                      const parsedContent = parseHtmlData(section.result, "docx").flat();

                      if (parsedContent.length === 1 && typeof parsedContent[0].children !== 'undefined') {
                          const textContent = parsedContent[0].children
                              .filter(child => child.text)
                              .map(child => child.text)
                              .join('');

                          const sentences = textContent.split(/(?<=\.)\s+(?=[A-Z])/);

                          if (sentences.length > 1) {
                              sectionElements.push(...sentences.map(sentence =>
                                  new Paragraph({
                                      children: [new TextRun({ text: sentence, size: 20 })],
                                      spacing: { after: 60 }
                                  })
                              ));
                          } else {
                              sectionElements.push(...parsedContent);
                          }
                      } else {
                          sectionElements.push(...parsedContent);
                      }
                  }

                  return sectionElements;
              } else {
                  return [
                    new Paragraph({
                      children: [new TextRun({ text: section.tagText, size: 22 })],
                    }),
                    ...section.result.map((item) =>
                      new Paragraph({
                        children: [new TextRun({ text: item, size: 20 })], // use actual item text
                        bullet: { level: 0 },
                      })
                    ),
                    section.endLine
                      ? new Paragraph({
                          children: [new TextRun({ text: section.endLine, size: 22 })],
                          spacing: { before: 200, after: 200 },
                        })
                      : null,
                  ].filter(Boolean);

              }
          }).flat();
        mainSections.forEach(
          (section) => {
            htmlContentCell.addChildElement(section)
          }
        )
        htmlContentRow.addChildElement(htmlContentCell)

        draftTable.addChildElement(htmlContentRow)
        // Add outline and subheading section to doc
        doc.addSection({ children: [draftTable] })



        const timestamp = moment().format("YYYY_M_D_h_mm_ss");
        const fileName = `${this.pageType}_${reportType}_keyword_${responseData.data.mainKeyword.replace(" ", "_")}_${timestamp}.docx`;
        downloadWordFile(doc, fileName)
      })
    },
    generateAIPageDraftWord(ommitInstructions = false) {
      if (this.useEditorAiDraft) {
        downloadEditorReport(this.$store, ReportTypes.AI_DRAFT, "word", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
      } else {
        this.generatePageDraftWord(this.siloKeyword.id, false, "ai_draft", ommitInstructions)
      }
    },
    generateAIDraftZip() {
      downloadEditorReport(
        this.$store,
        ReportTypes.AI_DRAFT,
        "zip",
        this.siloKeyword.id,
        this.siloKeyword.pageRunResult.secretKey,
        false,
        this.siloKeyword.pageRunId,
      )
    },
    generateEditedPageDraftWord(ommitInstructions = false) {
      if (this.useEditorHumanDraft) {
        downloadEditorReport(this.$store, ReportTypes.HUMAN_DRAFT, "word", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
      } else {
        this.generatePageDraftWord(this.siloKeyword.id, true, "human_draft", ommitInstructions)
      }
    },
    generateHumanEditedZip() {
      downloadEditorReport(
        this.$store,
        ReportTypes.HUMAN_DRAFT,
        "zip",
        this.siloKeyword.id,
        this.siloKeyword.pageRunResult.secretKey,
        false,
        this.siloKeyword.pageRunId,
      )
    },
    // PDF
    getSiloTopLevelReport(ommitInstructions = false) {
      this.getSiloPDFReport(this.siloKeyword.id, 'top_level', ommitInstructions);
    },
    getSiloTopLevelOutlineReport(ommitInstructions = false) {
      if (this.siloKeyword.aiRunResult.aiOutline) {
        if (this.useEditorOutline) {
          downloadEditorReport(this.$store, ReportTypes.OUTLINE, "pdf", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
        } else {
          this.getSiloPDFReport(this.siloKeyword.id, 'outline', ommitInstructions);
        }
      } else if (this.siloKeyword.hasEditedSource.hasOutline) {
        downloadEditorReport(this.$store, ReportTypes.OUTLINE, "pdf", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
      }
    },
    generateOutlineZip() {
      downloadEditorReport(
        this.$store,
        ReportTypes.OUTLINE,
        "zip",
        this.siloKeyword.id,
        this.siloKeyword.pageRunResult.secretKey,
        false,
        this.siloKeyword.pageRunId,
      )
    },
    generateAIDraftPDF(ommitInstructions = false) {
      if (this.siloKeyword.aiRunResult.aiGenDraft) {
        if (this.useEditorAiDraft) {
          downloadEditorReport(this.$store, ReportTypes.AI_DRAFT, "pdf", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
        } else {
          this.getSiloPDFReport(this.siloKeyword.id, 'ai_draft', ommitInstructions);
        }
      } else if (this.siloKeyword.hasEditedSource.hasAiDraft) {
        downloadEditorReport(this.$store, ReportTypes.AI_DRAFT, "pdf", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
      }
    },
    generateHumanEditedPDF(ommitInstructions = false) {
      if (this.siloKeyword.aiRunResult.aiGenDraft) {
        if (this.useEditorHumanDraft) {
          downloadEditorReport(this.$store, ReportTypes.HUMAN_DRAFT, "pdf", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
        } else {
          this.getSiloPDFReport(this.siloKeyword.id, 'human_draft', ommitInstructions);
        }
      } else if (this.siloKeyword.hasEditedSource.hasHumanDraft) {
        downloadEditorReport(this.$store, ReportTypes.HUMAN_DRAFT, "pdf", this.siloKeyword.id, this.siloKeyword.pageRunResult.secretKey, ommitInstructions)
      }
    },
    isNumber(n) {
      return !isNaN(parseFloat(n)) && !isNaN(n - 0)
    },
    getValidSchemas(schemas) {
      if (!Array.isArray(schemas)) return []
      return schemas.filter(item => {
        if (item.result === true) return true
        if (this.isNumber(item.result) && item.result > 0) return true
        return false
      })
    },
    commonEatSchemaTypes(data) {
      return data.pageRunResult.commonEatSchemaTypes || []
    },
    missingSignals(data) {
      const signalsCurrentPageHas = this.getValidSchemas(this.mainPageSchemaTypes(data)).map(item => item.signal)
      return this.commonEatSchemaTypes(data).filter(item => {
        if (linksCountSignal.includes(item.signal)) {
          return false;
        }
        if (signalsCurrentPageHas.includes(item.signal)) {
          return false;
        }
        if (item.count <= 1) {
          return false;
        }
        return true;
      })
    },
    generateEEATPDF(ommitInstructions = false) {
      this.getReportData(this.siloKeyword.id, 'eeat').then((responseData) => {
        const doc = new jsPDF();
        doc.setDrawColor(0);
        doc.setFillColor(1, 35, 35);
        const startX = 14;
        let currentY = 10;
        doc.addImage(responseData.headerImg, 'png', startX, currentY, 182, 71);
        let theme = responseData?.theme ?? {
            primary: "#093464",
            secondary: "#F0F6FF"
          };
        currentY += 70;

        if (!ommitInstructions) {
          const instructionsTableBulletPoints = [
            {
              text: [
                { text: 'While ticking off Google\'s EEAT items (as addressed in ', style: {} },
                { text: 'Google\'s Quality Rater ', style: { fontStyle: 'bold', textColor: '#FF5700', isLink: true, link: 'https://static.googleusercontent.com/media/guidelines.raterhub.com/en//searchqualityevaluatorguidelines.pdf' } },
                { text: 'Guidelines', style: { fontStyle: 'bold', textColor: '#FF5700', isLink: true, link: 'https://static.googleusercontent.com/media/guidelines.raterhub.com/en//searchqualityevaluatorguidelines.pdf' } },
                { text: ') on your website may not directly benefit rankings, by missing too many of', style: {} },
                { text: 'these signals you', style: {} },
                { text: 'risk being ignored by Google', style: { fontStyle: 'bold' } },
                { text: ', which will obviously affect your ability to', style: {} },
                { text: 'generate SEO traffic.', style: {} },
              ]
            },
            {
              text: [
                { text: 'In essence, Google is trying to decipher whether you are a', style: {} },
                { text: ' real business', style: { fontStyle: 'bold' } },
                { text: 'whether you are', style: {} },
                { text: 'who you say you are and that you can be trusted to deliver what you say you are going to', style: {} },
                { text: 'deliver.', style: {} },
              ]
            },
            {
              text: [
                { text: 'We have found the EEAT signals below on ', style: {} },
                { text: 'your top competitor\'s', style: { fontStyle: 'bold' } },
                { text: 'websites and have', style: {} },
                { text: 'tagged them as being either present or missing from your own website.', style: {} },
              ]
            },
            {
              text: [
                { text: 'We have given a description of each signal and your job is to try and ', style: {} },
                { text: 'implement', style: { fontStyle: 'bold' } },
                { text: 'as many', style: {} },
                { text: 'of the missing signals as possible. Most of them are very easy and can be', style: {} },
                { text: 'knocked off in a matter of minutes.', style: {} },
              ]
            }
          ];

          let instructionsTableRows = [
            [
              {
                content: '',
                styles: {
                  minCellWidth: 30,
                  minCellHeight: 20,
                  cellPadding: 1,
                  halign: 'right',
                  valign: 'middle',
                  fillColor: theme.primary,
                  textColor: '#FFFFFF',
                },
              },
              {
                content: 'Instructions',
                styles: {
                  minCellWidth: 5,
                  minCellHeight: 20,
                  halign: 'left',
                  cellPadding: 1,
                  fillColor: theme.primary,
                  fontSize: 22.65,
                  fontStyle: 'bold',
                  textColor: '#FFFFFF',
                },
              },
            ],
            [
              {
                colSpan: 2,
                content: '',
                styles: {
                  fillColor: theme.secondary,
                  minCellHeight: 80,
                  fontSize: 12,
                  lineHeight: 20,
                  fontStyle: 'normal',
                  textColor: '#000000',
                },
              },
            ],
          ];

          // Define the styles for the table
          let instructionsTableOptions = {
            startY: currentY + 8, // You can adjust this to place the table where you want in the PDF
            tableLineColor: theme.primary,
            tableLineWidth: 0.5,
            styles: {
              cellPadding: 5,
              valign: 'middle',
              fontSize: 12,
              textColor: [0, 0, 0], // Black text color
            },
            body: instructionsTableRows,
            didDrawCell: (imageSrc => function (data) {
              const doc = data.doc;

              if (data.row.index === 0 && data.column.index === 0) {
                const { x, y, width, height } = data.cell;
                const imgWidth = height - 4;
                const imgX = x + width - imgWidth - 2;
                doc.addImage(imageSrc, 'png', imgX + 8, y + 4, 10, 10);
              }
              if (data.row.index === 1) {
                let startY = data.cell.y + 10
                let startX = data.cell.x + 6
                instructionsTableBulletPoints.forEach((point) => {
                  // Draw the bullet
                  doc.circle(startX, startY - 1.5, 1, 'F');
                  let currentX = startX + 3; // Start position after bullet
                  point.text.forEach((part, idx) => {
                    if (part.style.fontStyle === 'bold') {
                      doc.setFont('helvetica', 'bold');
                    } else {
                      doc.setFont('helvetica', 'normal');
                    }
                    if (part.style.textColor) {
                      doc.setTextColor(part.style.textColor);
                    } else {
                      doc.setTextColor('#000000');
                    }
                    if (idx) currentX++

                    doc.text(part.text, currentX, startY);

                    if (part.style.isLink) {
                      const textWidth = doc.getTextWidth(part.text);
                      doc.setTextColor(part.style.textColor || '#0000FF');
                      doc.text(part.text, currentX, startY);

                      doc.link(currentX, startY - 4, textWidth, 8, { url: part.style.link });
                    }

                    // Update X position for the next part
                    currentX += doc.getTextWidth(part.text);
                    if (idx !== point.text.length - 1 && currentX > 170) {
                      currentX = startX + 3
                      startY += 5
                    }
                  });

                  // Move Y position for the next bullet point
                  startY += 8; // Adjust spacing between bullet points
                });
              }
            })(this.bulbIcon),
          };

          autoTable(doc, instructionsTableOptions);
        }

        let rows = [
          [
            {
              colSpan: 2,
              content: "Your site's EEAT audit",
              styles: {
                halign: 'center',
                fillColor: theme.secondary,
                fontSize: 22.65,
                fontStyle: 'bold',
                textColor: '#000000',
              },
            },
          ],
          [
            {
              colSpan: 2,
              content: '',
              styles: {
                halign: 'center',
                cellPadding: 1,
                valign: 'middle',
                fillColor: theme.secondary,
                fontSize: 12,
                fontStyle: 'bold',
                minCellWidth: responseData.data.pageRunResult.url?.length > 50 ? 15 : 35,
                textColor: '#FF5700',
                marginLeft: 30
              },
            },
            {
              content: this.extractHostnameWithProtocol(responseData.data.pageRunResult.url),
              styles: {
                halign: 'center',
                valign: 'middle',
                fillColor: theme.secondary,
                cellPadding: 1,
                fontSize: 12,
                fontStyle: 'bold',
                minCellWidth: responseData.data.pageRunResult.url?.length > 50 ? 80 : 35,
                textColor: theme.primary,
              },
            },
          ],
          [
            {
              colSpan: 2,
              content:
                "We have found these EEAT signals on your top competitors' sites. The ones marked 'Add' have not been found on your site and as many as possible should be added before you begin publishing new content.",
              styles: {
                halign: 'center',
                fillColor: theme.secondary,
                fontSize: 12,
                textColor: '#000000',
              },
            },
          ],
          [
            {
              colSpan: 2,
              content:
                "For instructions on what each of the EEAT signals looks like on the page,",
              styles: {
                cellPadding: { top: 3, right: 3, bottom: 3, left: 17 },
                fillColor: theme.secondary,
                fontSize: 12,
                textColor: '#000000',
              },
            },
          ],
        ];
          let tableOptions = {
            startY: ommitInstructions ? currentY + 8 : undefined,
            tableLineColor: theme.primary,
            tableLineWidth: 0.5,
            styles: {
              cellPadding: { top: 10, right: 8, bottom: 10, left: 8 },
              valign: 'middle',
              fontSize: 12,
              textColor: [0, 0, 0],
            },
            body: rows,
            didDrawCell: ((siteIcon, url) => function(data) {
            const doc = data.doc;

            if (data.row.index === 1 && data.column.index === 0) {
              const { x, y, width, height } = data.cell;
              const centerX = x + width / 2;

              // Measure URL text width
              doc.setFont('helvetica', 'bold');
              const urlWidth = doc.getTextWidth(url);

              // Calculate total width (icon + space + URL)
              const iconSize = 4;
              const spacing = 2;
              const totalWidth = iconSize + spacing + urlWidth;

              // Draw icon (centered with text)
              doc.addImage(
                siteIcon,
                'png',
                centerX - totalWidth / 2,
                y + (height - iconSize) / 2,
                iconSize,
                iconSize
              );

              // Draw URL text (next to icon)
              doc.setTextColor('#FF5700');
              doc.text(
                url,
                centerX - totalWidth / 2 + iconSize + spacing,
                y + height / 2 + 2
              );

              // Make the entire area clickable
              doc.link(
                centerX - totalWidth / 2,
                y,
                totalWidth,
                height,
                { url: url }
              );
            }

            // Keep "click here" right-aligned (original positioning)
            else if (data.row.index === 3 && data.column.index === 0) {
              const { x, y } = data.cell;
              doc.setTextColor('#FF5700');
              doc.textWithLink(
                'click here',
                x + 155,
                y + 7,
                { url: 'https://help.pageoptimizer.pro/article/what-eeat-signals-look-like' }
              );
            }
          })(responseData.data.siteIcon, this.extractHostnameWithProtocol(responseData.data.pageRunResult.url)),
      };
        autoTable(doc, tableOptions);

        rows = this.getValidSchemas(this.mainPageSchemaTypes(responseData.data)).concat(this.missingSignals(responseData.data))
          .map((row) => ({
            name: {
              content: row.signal,
              styles: {
                halign: 'left',
                valign: 'middle',
                fillColor: theme.primary,
                fontSize: 12,
                fontStyle: 'bold',
                textColor: '#FFFFFF',
                cellPadding: { top: 7, right: 9, bottom: 7, left: 9 },
              },
            },
            description: {
              content: this.descriptionObj?.[row.signal] || '',
              styles: {
                fillColor: theme.secondary,
                fontSize: 12,
                fontStyle: 'normal',
                valign: 'middle',
                halign: 'left',
                cellPadding: { top: 5, right: 5, bottom: 5, left: 5 },
              },
            },
            status: {
              content: row.result,
              styles: {
                fillColor: row.result ? '#D7F4E8' : '#FFC4C4',
                fontSize: 8.7,
                fontStyle: 'bold',
                valign: 'middle',
                halign: 'center',
                textColor: '#FFFFFF',
                cellPadding: { top: 7, right: 9, bottom: 7, left: 9 },
              },
            },
          }));

        // Define the styles for the table
const spacerRow = [
  { content: '', styles: { fillColor: '#FFFFFF', minCellHeight: 0, lineWidth: 0 } },
  { content: '', styles: { fillColor: '#FFFFFF', minCellHeight: 0, lineWidth: 0 } },
  { content: '', styles: { fillColor: '#FFFFFF', minCellHeight: 0, lineWidth: 0 } },
];

tableOptions = {
  startY: doc.lastAutoTable.finalY,
  tableLineColor: theme.primary,
  tableLineWidth: 0.5,

  head: [
    [
      { content: 'Name', styles: { fillColor: theme.primary, textColor: '#FFFFFF', fontSize: 12, fontStyle: 'bold', halign: 'left' } },
      { content: 'Description', styles: { fillColor: theme.primary, textColor: '#FFFFFF', fontSize: 12, fontStyle: 'bold', halign: 'left' } },
      { content: 'Status', styles: { fillColor: theme.primary, textColor: '#FFFFFF', fontSize: 12, fontStyle: 'bold', halign: 'left' } },
    ],
  ],

  body: [spacerRow, ...rows.map((row) => [
    row.name.content,
    row.description.content ? row.description.content + "*" : "",
    {
      content: row.status.content,
      styles: {
        ...row.status.styles,
        margin: 2,
        padding: 4,
        textAlign: 'center',
        borderRadius: 10,
        lineHeight: 1.2,
      },
    },
  ])],

  styles: {
    cellPadding: 3.5,
    fontSize: 12,
    textColor: [0, 0, 0],
    fontStyle: 'normal',
  },

  columnStyles: {
    0: { fillColor: theme.primary, textColor: '#FFFFFF', fontSize: 12, fontStyle: 'bold', halign: 'left', valign: 'middle' },
    1: { fillColor: theme.secondary, fontSize: 12, fontStyle: 'normal', valign: 'middle', halign: 'left' },
    2: { fontSize: 8.7, fontStyle: 'bold', valign: 'middle', halign: 'center', textColor: '#FFFFFF' },
  },

  didDrawCell: (function (addImage, presentImage) {
    return function (data) {
      const doc = data.doc;
      const cell = data.cell;

      // Handle spacer row logic
      if (data.row.section === 'body' && data.row.index === 0) {
        const normalRowHeight = data.table.body[1]?.height || 20;
        data.row.height = 1.5; // Set height for spacer row
        return;
      }

      // Draw white line below the first column header
      if (data.row.section === 'body' && data.column.index === 0) {
        doc.setDrawColor('#FFFFFF');
        doc.setLineWidth(1.5);
        doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
      }

      // Draw custom pills for Status column
      if (data.column.dataKey === 2 && data.row.section === 'body') {
        const centerX = cell.x + cell.width / 2;
        const centerY = cell.y + cell.height / 2;
        const isCheck = data.row?.raw?.[2]?.content;

        const pillWidth = isCheck ? 18 : 14;
        const pillHeight = 7;
        doc.setFillColor(isCheck ? '#25A97A' : '#FF3939');
        doc.roundedRect(centerX - pillWidth / 2, centerY - pillHeight / 2, pillWidth, pillHeight, pillHeight / 2, pillHeight / 2, 'F');

        if (isCheck) {
          doc.addImage(presentImage, 'png', centerX - pillWidth / 2 + 1, centerY - pillHeight / 2 + 2, 3, 3);
        } else {
          doc.addImage(addImage, 'png', centerX - pillWidth / 2 + 2, centerY - pillHeight / 2 + 2, 3, 3);
        }

        doc.setTextColor('#FFFFFF');
        doc.setFontSize(8.7);
        doc.text(isCheck ? 'Present' : 'Add', centerX + (isCheck ? 1.5 : 2), centerY + 1.2, { align: 'center' });
      }

      // Draw general borders for all non-spacer cells
      doc.setLineWidth(0.3);
      doc.setDrawColor(theme.primary);theme.secondary
      doc.setDrawColor(theme.primary);theme.secondary
      doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
      doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);
      doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
      doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
    };
  })(this.eeatKeywordWarningIcon, this.eeatKeywordPresentIcon),
};


        autoTable(doc, tableOptions);

        rows = [
          [
            {
              content: '*Usually, the signal is detected in these elements. We recommend incorporating them accordingly. However, our AI can identify these signals even if they are implemented unconventionally.',
              styles: { halign: 'center', valign: 'middle', fontStyle: 'italic', fillColor: null },
            },
          ],
        ];

        tableOptions = {
          startY: doc.lastAutoTable.finalY + 2,
          startX: 20,
          body: rows,
          columnStyles: { 0: { cellWidth: 170 } }
        };
        autoTable(doc, tableOptions);

        const timestamp = moment().format('YYYY_M_D_h_mm_ss');
        doc.save(`google_eeat_${timestamp}.pdf`);
      })
    },
    createEEATDocx(ommitInstructions = false) {
      this.getReportData(this.siloKeyword.id, 'eeat').then((responseData) => {
        const data = this.getValidSchemas(this.mainPageSchemaTypes(responseData.data)).concat(this.missingSignals(responseData.data))
        const doc = new Document({
          styles: { default: { document: { run: { font: "Arial" } } } },
          sections: [
            {
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: responseData.headerImg,
                      transformation: getDocxFullWidthImageTransformation(),
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: {
                    line: 240,
                    after: 480,
                  },
                }),

                ...!ommitInstructions
                  ? [
                    new Table({
                      rows: [
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "💡 Instructions",
                                      color: "#FFFFFF",
                                      bold: true,
                                      size: 38,
                                    }),
                                  ],
                                  heading: HeadingLevel.HEADING_1,
                                  alignment: AlignmentType.CENTER,
                                  spacing: {
                                    before: 120,
                                    after: 120,
                                    afterAutoSpacing: true,
                                  },
                                }),
                              ],
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              verticalAlign: "center",
                              shading: {
                                fill: "#093464",
                              },
                              margins: {
                                top: convertInchesToTwip(0.1),
                                bottom: convertInchesToTwip(0.1),
                                left: convertInchesToTwip(0.2),
                                right: convertInchesToTwip(0.2),
                              },
                              borders: {
                                top: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                bottom: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                left: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                right: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                              },
                            }),
                          ],
                        }),
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "While ticking off Google’s EEAT items (as addressed in",
                                    }),
                                    new TextRun({
                                      text: " Google’s Quality Rater Guidelines",
                                      bold: true,
                                      color: "#FF5700"
                                    }),
                                    new TextRun({
                                      text: ") on your website may not directly benefit rankings, by missing too many of these signals you ",
                                    }),
                                    new TextRun({
                                      text: "risk being ignored by Google",
                                      bold: true
                                    }),
                                    new TextRun({
                                      text: ", which will obviously affect your ability to generate SEO traffic.",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "In essence, Google is trying to decipher whether you are a",
                                    }),
                                    new TextRun({
                                      text: " real business ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: ", whether you are who you say you are and that you can be trusted to deliver what you say you are going to deliver.",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "We have found the EEAT signals below on",
                                    }),
                                    new TextRun({
                                      text: " your top competitor’s ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "websites and have tagged them as being either present or missing from your own website.",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: "We have given a description of each signal and your job is to try and",
                                    }),
                                    new TextRun({
                                      text: " implement ",
                                      bold: true,
                                    }),
                                    new TextRun({
                                      text: "as many of the missing signals as possible. Most of them are very easy and can be knocked off in a matter of minutes.",
                                    }),
                                    new TextRun({
                                      text: ".",
                                    }),
                                  ],
                                  bullet: {
                                    level: 0,
                                  },
                                  spacing: {
                                    before: 240,
                                  },
                                }),
                              ],
                              width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                              },
                              verticalAlign: "",
                              shading: {
                                fill: "#F0F6FF",
                              },
                              margins: {
                                top: convertInchesToTwip(0.15),
                                left: convertInchesToTwip(0.15),
                                bottom: convertInchesToTwip(0.15),
                                right: convertInchesToTwip(0.15),
                              },
                              borders: {
                                top: {
                                  color: "#093464",
                                  size: 5,
                                },
                                bottom: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                left: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                                right: {
                                  style: BorderStyle.SINGLE,
                                  color: "#093464",
                                  size: 5,
                                },
                              },
                            }),
                          ],
                        }),
                      ],
                      width: {
                        size: 100,
                        type: WidthType.PERCENTAGE,
                      },
                      columnWidths: DOCX_SINGLE_COLUMN_WIDTHS,
                    }),
                  ]
                  : [],
                new Paragraph({ children: [new TextRun({ text: "  " })] }),
                new Table({
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              alignment: AlignmentType.CENTER,
                              children: [
                                new TextRun({
                                  font: "Arial",
                                  text: "Your site's EEAT audit",
                                  size: "20pt",
                                  bold: true
                                })
                              ],
                              spacing: {
                                before: 60,
                                after: 120,
                              }
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: " "
                                })
                              ]
                            }),
                            new Paragraph({
                              alignment: AlignmentType.CENTER,
                              children: [
                                new ExternalHyperlink({
                                  children: [
                                    new ImageRun({
                                      data: responseData.data.siteIcon,
                                      transformation: {
                                        width: 10,
                                        height: 10,
                                      },
                                    }),
                                    new TextRun({
                                      text: " "
                                    }),
                                    new TextRun({
                                      font: "Arial",
                                      text: this.extractHostnameWithProtocol(responseData.data.pageRunResult.url),
                                      style: "Hyperlink",
                                      bold: true,
                                      color: "#FF5700"
                                    })
                                  ],
                                  link: this.extractHostnameWithProtocol(responseData.data.pageRunResult.url)
                                })
                              ],
                              spacing: {
                                before: 60,
                                after: 120,
                              }
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: " "
                                })
                              ]
                            }),
                            new Paragraph({
                              alignment: AlignmentType.CENTER,
                              children: [
                                new TextRun({
                                  font: "Arial",
                                  text: "We have found these EEAT signals on your top competitors' sites. The ones marked ‘Add’ have not been found on your site and as many as possible should be added before you begin publishing new content.",
                                }),
                              ],
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: " "
                                })
                              ]
                            }),
                            new Paragraph({
                              alignment: AlignmentType.CENTER,
                              children: [
                                new TextRun({
                                  font: 'Arial',
                                  text: 'For instructions on what each of the EEAT signals looks like on the page, ',
                                }),
                                new ExternalHyperlink({
                                  children: [
                                    new TextRun({
                                      font: 'Arial',
                                      text: 'click here',
                                      style: 'Hyperlink',
                                      color: "#FF5700"
                                    }),
                                  ],
                                  link: 'https://help.pageoptimizer.pro/article/what-eeat-signals-look-like',
                                }),
                              ],
                              spacing: {
                                before: 120,
                                after: 120,
                              }
                            })
                          ],
                          verticalAlign: 'center',
                          width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                          },
                          shading: {
                            fill: "#F0F6FF",
                          },
                          margins: {
                            top: convertInchesToTwip(0.20),
                            left: convertInchesToTwip(0.20),
                            bottom: convertInchesToTwip(0.20),
                            right: convertInchesToTwip(0.20),
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              style: BorderStyle.SINGLE,
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          }
                        }),

                      ],
                    }),
                  ],
                  columnWidths: DOCX_SINGLE_COLUMN_WIDTHS,
                }),

                new Paragraph({
                  children: [
                    new PageBreak()
                  ]
                }),

                // Third Table: 3 columns, 3 rows
                new Table({
                  rows: [
                    // Header row
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({ font: "Arial", text: "Name", bold: true, color: "#FFFFFF" }),
                              ],
                            }),
                          ],
                          width: { size: 15, type: "pct" },
                          shading: {
                            fill: "#093464"
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              style: BorderStyle.SINGLE,
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 5,
                            },

                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                          margins: {
                            top: convertInchesToTwip(0.10),
                            left: convertInchesToTwip(0.10),
                            bottom: convertInchesToTwip(0.10),
                            right: convertInchesToTwip(0.10),
                          },
                          verticalAlign: "center",
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  font: "Arial", text: "Description", bold: true, color: "#FFFFFF", borders: {
                                    bottom: {
                                      color: "#093464",
                                      style: BorderStyle.SINGLE,
                                      size: 5,
                                    }
                                  }
                                }),
                              ],
                            }),
                          ],
                          width: { size: 70, type: "pct" },
                          shading: {
                            fill: "#093464"
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              style: BorderStyle.SINGLE,
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 5,
                            },
                            margins: {
                                bottom: convertInchesToTwip(30),
                              },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                          margins: {
                            top: convertInchesToTwip(0.10),
                            left: convertInchesToTwip(0.10),
                            bottom: convertInchesToTwip(0.10),
                            right: convertInchesToTwip(0.10),
                          },
                          verticalAlign: "center",
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({ font: "Arial", text: "Status", bold: true, color: "#FFFFFF" }),
                              ],
                            }),
                          ],
                          width: { size: 15, type: "pct" },
                          shading: {
                            fill: "#093464"
                          },
                          borders: {
                            top: {
                              color: "#093464",
                              style: BorderStyle.SINGLE,
                              size: 5,
                            },
                            bottom: {
                              style: BorderStyle.SINGLE,
                              color: "#FFFFFF",
                              size: 5,
                            },
                            margins: {
                                bottom: convertInchesToTwip(30),
                              },
                            left: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                            right: {
                              style: BorderStyle.SINGLE,
                              color: "#093464",
                              size: 5,
                            },
                          },
                          margins: {
                            top: convertInchesToTwip(0.10),
                            left: convertInchesToTwip(0.10),
                            bottom: convertInchesToTwip(0.10),
                            right: convertInchesToTwip(0.10),
                          },
                          verticalAlign: "center",
                        }),
                      ],
                    }),
                    new TableRow({
            children: [
                new TableCell({
                    children: [
                        new Paragraph({ text: "" }),
                    ],
                    shading: { fill: "#FFFFFF" }, // White background
                    borders: {
                        top: { style: BorderStyle.NIL }, // No top border
                        bottom: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black bottom border
                        left: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black left border
                        right: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black right border
                    },
                    margins: {
                        top: convertInchesToTwip(0.001),
                        left: convertInchesToTwip(0.001),
                        bottom: convertInchesToTwip(0.001),
                        right: convertInchesToTwip(0.001)
                    },
                }),
                new TableCell({
                    children: [
                        new Paragraph({ text: "" }),
                    ],
                    shading: { fill: "#FFFFFF" }, // White background
                    borders: {
                        top: { style: BorderStyle.NIL }, // No top border
                        bottom: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black bottom border
                        left: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black left border
                        right: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black right border
                    },
                    margins: {
                      top: convertInchesToTwip(0.0001),
                      left: convertInchesToTwip(0.0001),
                      bottom: convertInchesToTwip(0.0001),
                      right: convertInchesToTwip(0.0001)
                    },
                }),
                new TableCell({
                    children: [
                                new Paragraph({ text: "" }),
                            ],
                            shading: { fill: "#FFFFFF" }, // White background
                            borders: {
                                top: { style: BorderStyle.NIL }, // No top border
                                bottom: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black bottom border
                                left: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black left border
                                right: { style: BorderStyle.SINGLE, color: "#ffffff", size: 1.5 }, // Black right border
                            },
                            margins: {
                             top: convertInchesToTwip(0.0001),
                            left: convertInchesToTwip(0.0001),
                            bottom: convertInchesToTwip(0.0001),
                            right: convertInchesToTwip(0.0001)
                            },
                        }),
                    ],
                }),
                    ...data.map((row) => {
                      return row.signal && (new TableRow({
                        children: [
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    font: "Arial",
                                    text: row.signal,
                                    bold: true,
                                    color: "#FFFFFF"
                                  }),
                                ],
                              }),
                            ],
                            shading: {
                              fill: "#093464"
                            },
                            verticalAlign: 'center',
                            margins: {
                              top: convertInchesToTwip(0.0001),
                              left: convertInchesToTwip(0.0001),
                              bottom: convertInchesToTwip(0.0001),
                              right: convertInchesToTwip(0.0001)
                            },
                            borders: {
                              top: {
                                color: "#FFFFFF",
                                style: BorderStyle.SINGLE,
                                size: 5,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#FFFFFF",
                                size: 10,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#FFFFFF",
                                size: 10,
                              },
                            }
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                font: "Arial",
                                text: this.descriptionObj?.[row.signal] ? this.descriptionObj?.[row.signal] + "*" : '',
                              }),
                            ],
                            shading: {
                              fill: "#F0F6FF"
                            },
                            margins: {
                              top: convertInchesToTwip(0.10),
                              left: convertInchesToTwip(0.10),
                              bottom: convertInchesToTwip(0.10),
                              right: convertInchesToTwip(0.10),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                style: BorderStyle.SINGLE,
                                size: 5,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                            }
                          }),
                          console.log(row.result, 'row.result'),
                          new TableCell({
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.CENTER,
                                font: "Arial",
                                children: [
                                  new ImageRun({
                                    data: row.result ? responseData.data.presentIcon : responseData.data.addIcon,
                                    transformation: {
                                      width: row.result ? 80 : 60,
                                      height: 25,
                                    },
                                  }),
                                ],
                              }),
                            ],
                            verticalAlign: 'center',
                            shading: {
                              fill: row.result ? "#D7F4E8" : "#FFC4C4"
                            },
                            margins: {
                              top: convertInchesToTwip(0.10),
                              left: convertInchesToTwip(0.10),
                              bottom: convertInchesToTwip(0.10),
                              right: convertInchesToTwip(0.10),
                            },
                            borders: {
                              top: {
                                color: "#093464",
                                style: BorderStyle.SINGLE,
                                size: 5,
                              },
                              bottom: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              left: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                              right: {
                                style: BorderStyle.SINGLE,
                                color: "#093464",
                                size: 5,
                              },
                            }
                          }),
                        ],
                      }))
                    }),
                  ],
                  width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                  },
                  columnWidths: DOCX_EEAT_SIGNAL_COLUMN_WIDTHS,
                }),

                new Paragraph({
                  text: "",
                  heading: HeadingLevel.HEADING_2,
                }),

                new Paragraph({
                  children: [
                    new TextRun({
                      size: 18,
                      font: "Arial",
                      text: "*Usually, the signal is detected in these elements. We recommend incorporating them accordingly. However, our AI can identify these signals even if they are implemented unconventionally.",
                      italics: true,
                    }),
                  ],
                }),
              ],
            },
          ],
        });

        const mimeType =
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const timestamp = moment().format('YYYY_M_D_h_mm_ss');
        const fileName = `google_eeat_${timestamp}.docx`;
        Packer.toBlob(doc).then((blob) => {
          const docblob = blob.slice(0, blob.size, mimeType);
          saveAs(docblob, fileName);
        });
      })
    },
    generateDefaultSchemasPDF(ommitInstructions = false) {
      getReportHeaderImage(this.$store, "schema").then((responseData) => {
        this.generateSchemasPDFBase(
          defaultSchemas,
          responseData.headerImg,
          responseData.theme,
          'schema_report',
          ommitInstructions
        )
      })
    },
    generateSchemasPDF(ommitInstructions = false) {
      this.getReportData(this.siloKeyword.id, 'schema').then((responseData) => {
        this.generateSchemasPDFBase(
          responseData.data.schemas,
          responseData.headerImg,
          responseData.theme,
          'combined_schema_report',
          ommitInstructions
        )
      })
    },
    getSchemasWord(includeInstructions= true, ommitInstructions = false) {
      this.getReportData(this.siloKeyword.id, 'schema').then((responseData) => {
        generateSchemasWord(
          {theme: responseData.theme,
          data: {
            schemas: responseData.data.schemas,
            mainkeyword: responseData.data.mainkeyword,
            pageType: responseData.data.pageType,
          },
          includeInstructions,
          headerImg: responseData.headerImg,
          omitInstructions: !ommitInstructions
        }
        )
      })
    },
    generateSchemasPDFBase(schemas, headerImg, themedata, filename_prefix, ommitInstructions = false) {
      function hexToRgb(hex) {
          const match = hex.replace('#', '').match(/.{1,2}/g);
          return match ? match.map((x) => parseInt(x, 16)) : [0, 0, 0];
      }
      console.log(themedata)
      let theme = themedata ? {
          primary: hexToRgb(themedata.primary),
          secondary: hexToRgb(themedata.secondary),
        }:{
      primary: hexToRgb("#093464"),
      secondary: hexToRgb("#F0F6FF"),
    };
      const doc = new jsPDF();
      doc.setDrawColor(0);
      doc.setFillColor(1, 35, 35);
      const startX = 14;
      let currentY = 10;

      if (headerImg) {
        doc.addImage(headerImg, 'png', startX, currentY, 182, 71);
        currentY += 70;
      }

      if (!ommitInstructions) {
        const instructionsBullets = [
          'POP analyzes the schema used on top competing pages.',
          'We then filter and build the optimal custom schema for you to add to the page.',
          ''
        ];

        let rows = [
          [
            {
              content: '',
              styles: {
                minCellWidth: 40,
                minCellHeight: 20,
                cellPadding: 1,
                halign: 'right',
                valign: 'middle',
                fillColor: theme.primary,
                textColor: '#FFFFFF',
              },
            },
            {
              content: 'Add Schema to this page!',
              styles: {
                minCellWidth: 40,
                minCellHeight: 20,
                cellPadding: 1,
                halign: 'left',
                valign: 'middle',
                fillColor: theme.primary,
                fontSize: 22.65,
                fontStyle: 'bold',
                textColor: '#FFFFFF',
              },
            },
          ],
          [
            {
              colSpan: 2,
              content: instructionsBullets.map((bullet) => `${bullet}`).join('\n'),
              styles: {
                halign: 'center',
                fillColor: theme.secondary,
                fontSize: 12,
                lineHeight: 20,
                fontStyle: 'normal',
                textColor: '#000000',
              },
            },
          ],
        ];
        let instructionsTableOptions = {
          startY: currentY + 8,
          tableLineColor: theme.primary,
          tableLineWidth: 0.5,
          styles: {
            cellPadding: 5,
            valign: 'middle',
            fontSize: 12,
            textColor: [0, 0, 0],
          },
          body: rows,
          didDrawCell: (imageSrc => function (data) {
            const doc = data.doc;

            if (data.row.index === 0 && data.column.index === 0) {
              const { x, y, width, height } = data.cell;
              const imgWidth = height - 4;
              const imgX = x + width - imgWidth - 2;
              doc.addImage(imageSrc, 'png', imgX + 8, y + 4, 10, 10);
            }
            if (data.row.index === 1 && data.column.index === 0) {
              // First part of the text
              const textBeforeLink = 'For instructions on how to publish schema to your page, ';
              const linkText = 'click here.';
              const linkY = data.cell.y + data.cell.height - 6;
              const cellWidth = data.cell.width;
              const totalTextWidth = doc.getTextWidth(textBeforeLink) + doc.getTextWidth(linkText);
              const centeredX = data.cell.x + (cellWidth - totalTextWidth) / 2; // Center the total text width

              // Add the first part of the text
              doc.text(textBeforeLink, centeredX, linkY);

              // Calculate the width of the text before the link
              const linkStartX = centeredX + doc.getTextWidth(textBeforeLink);

              // Add the link text
              doc.setTextColor(255, 87, 0);
              doc.text(linkText, linkStartX, linkY);

              const underlineY = linkY + 1;
              const linkWidth = doc.getTextWidth(linkText);
              const lineHeight = 0.5;
              doc.setDrawColor(255, 87, 0);
              doc.setLineWidth(lineHeight);
              doc.line(linkStartX, underlineY, linkStartX + linkWidth, underlineY);

              // Create the clickable link
              doc.link(linkStartX, linkY - 10, doc.getTextWidth(linkText), 10, { url: 'https://help.pageoptimizer.pro/article/how-to-add-pageoptimizer-pros-schema-to-your-pages' }); // Replace with your actual URL
            }
          })(this.bulbIcon),
        };

        autoTable(doc, instructionsTableOptions);
      }


       let rows = schemas.filter((schema) => schema.schema)
    .map((schema) => {
      return [
        [
          {
            content: schema.type,
            styles: {
              fillColor: theme.secondary,
              fontSize: 22.65,
              fontStyle: 'bold',
              textColor: '#000000',
            },
          },
        ],
        [
          {
            content: '<script type="application/ld+json">',
            styles: {
              fillColor: theme.secondary,
              fontSize: 12,
              textColor: '#000000',
              cellPadding: 1,
              lineHeight: 1.1,
            },
          },
        ],
        [
          {
            content: schema.schema,
            styles: {
              fillColor: theme.secondary,
              fontSize: 12,
              textColor: '#000000',
            },
          },
        ],
        [
          {
            content: '</' + 'script>',
            styles: {
              fillColor: theme.secondary,
              fontSize: 12,
              textColor: '#000000',
              cellPadding: 1,
              lineHeight: 1.1,
            },
          },
        ],
      ];
    })
    .flat();

      let tableOptions = {
        startY: ommitInstructions ? currentY + 8 : undefined,
        tableLineColor: theme.primary,
        tableLineWidth: 0.5,
        styles: {
          cellPadding: 5,
          valign: 'middle',
          fontSize: 12,
          textColor: [0, 0, 0], // Black text color
        },
        body: rows,
      };

      autoTable(doc, tableOptions);

      const timestamp = moment().format('YYYY_M_D_h_mm_ss');
      doc.save(`${filename_prefix}_${timestamp}.pdf`);
    },
    generateDefaultSchemasJSONReport() {
      generateSchemasJSON({
        data: {
          schemas: defaultSchemas,
          report_title: 'schemas'
        }
      })
    },
    generateSchemasJSONReport() {
      this.getReportData(this.siloKeyword.id, 'schema').then((responseData) => {
        generateSchemasJSON(responseData)
      })
    },
    /** A row action from the panel or its formats side menu: run it and close the panel. */
    runAction(name, params) {
      if (!name) return
      this.menuOpen = false
      this.getMethod(name, params)
    },
    getMethod(name, params) {
      const func = {
        getSchemasWord: this.getSchemasWord,
        generateTopLevelPDFBriefReport: this.getSiloTopLevelReport,
        generateTopLevelWordBriefReport: this.generateTopLevelWordBriefReport,
        generateOutlinePDF: this.getSiloTopLevelOutlineReport,
        generateOutlineWord: this.generateOutlineWord,
        generateOutlineZip: this.generateOutlineZip,
        generateAIDraftPDF: this.generateAIDraftPDF,
        generateAIPageDraftWord: this.generateAIPageDraftWord,
        generateAIDraftZip: this.generateAIDraftZip,
        generateHumanEditedPDF: this.generateHumanEditedPDF,
        generateEditedPageDraftWord: this.generateEditedPageDraftWord,
        generateHumanEditedZip: this.generateHumanEditedZip,
        gotoAiRun: this.gotoAiRun,
        generateEEATPDF: this.generateEEATPDF,
        createEEATDocx: this.createEEATDocx,
        generateSchemasPDF: this.generateSchemasPDF,
        generateSchemasJSONReport: this.generateSchemasJSONReport,
        generateDefaultSchemasJSONReport: this.generateDefaultSchemasJSONReport,
        generateDefaultSchemasPDF: this.generateDefaultSchemasPDF,
        gotoContentEditor: this.gotoContentEditor,
        gotoChecklistEditor: this.gotoChecklistEditor
      }[name];
      func?.apply(this, params || []);
    },
  },
  beforeMount() {
    this.descriptionObj = explainData
  },
  mounted() {
    getBase64FromUrl(eeatPdfHeader, (base64) => {
      this.eeatReportLogo = base64;
    });
    getBase64FromUrl(eeatWorldIcon, (base64) => {
      this.eeatWorldIcon = base64;
    });
    getBase64FromUrl(eeatKeywordWarningIcon, (base64) => {
      this.eeatKeywordWarningIcon = base64;
    });
    getBase64FromUrl(eeatKeywordPresentIcon, (base64) => {
      this.eeatKeywordPresentIcon = base64;
    });
    getBase64FromUrl(bulbIcon, (base64) => {
      this.bulbIcon = base64;
    });
    getBase64FromUrl(wordDocHeadImg, (base64) => {
      this.wordDocHeadImg = base64;
    });
    getBase64FromUrl(pdfHeaderLogo, (base64) => {
      this.schemasReportLogo = base64;
    });
     getBase64FromUrl(orangeKeyIcon, (base64) => {
      this.orangeKeyIcon = base64;
    });
    getBase64FromUrl(wordCountIcon, (base64) => {
      this.wordCountIcon = base64;
    });
    getBase64FromUrl(pageIcon, (base64) => {
      this.pageIcon = base64;
    });
    getBase64FromUrl(checkMarkIcon, (base64) => {
      this.checkMarkIcon = base64;
    });
    getBase64FromUrl(greenInfoIcon, (base64) => {
      this.greenInfoIcon = base64;
    });
  },
};
</script>

<style>
/* The panel is detached to the app root by v-menu, so its box is styled through the content class */
.v-menu__content.silo-action-menu {
  border-radius: 12px;
  border: 1px solid var(--v-gray15Color-base, #e4e4e4);
  box-shadow: 0 10px 30px rgba(1, 71, 81, 0.12);
}
</style>

<style scoped>
.v-list {
  padding-top: 0;
  padding-bottom: 0;
}
.basic-divider {
  border-top: 1px solid var(--v-gray15Color-base, #e4e4e4);
  margin-top: 6px;
  break-after: always;
}

/* design "Download content" panel (pa.css): teal bold rows, indented grey report rows,
   a download icon at the right of each downloadable row */
.silo-action-menu__list {
  padding: 8px 0;
  min-width: 380px;
}
.silo-action-menu__list--formats {
  min-width: 240px;
}
.silo-action-menu__list .v-list-item {
  min-height: 40px;
  padding: 0 20px;
}
.silo-action-menu__heading {
  pointer-events: none;
}
.silo-action-menu__heading .silo-action-menu__title {
  padding-top: 6px;
}
.silo-action-menu__item:hover {
  background: var(--v-gray7Color-base, #f7f7f7);
}
.silo-action-menu__row {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  cursor: pointer;
}
.silo-action-menu__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
}
.silo-action-menu__item--child {
  padding-left: 40px !important;
}
.silo-action-menu__item--child .silo-action-menu__title,
.silo-action-menu__item--format .silo-action-menu__title {
  font-weight: 600;
  color: #6b7b7e; /* --pop-ink-muted */
}
.silo-action-menu__lead {
  margin-right: 8px;
}
.silo-action-menu__download {
  flex: none;
  margin-left: 16px;
  font-size: 20px;
  color: var(--v-mainColor-base);
}

.box-wh35:has(.menu-img) {
  width: 30px;
  height: 30px;
  background-color: var(--v-lightYellowColor-base);
  padding: 6px;
}

.box-wh35:has(.menu-icon) {
  background-color: var(--v-lightYellowColor-base);
  border-radius: 2px;
  color: var(--v-profileAnchorColor-base);
  padding: 3px;
}

.url-link {
  margin-left: 5px;
  font-weight: 500;
  color: #0a6cdb;
  text-decoration: underline;
}
</style>
